import { z } from 'zod';

import { localeSchema } from './locales.js';
import { videoAspectRatioSchema, videoResolutionSchema, VIDEO_DURATION } from './video.js';

/**
 * ÜRÜN GÖRSELİ ANALİZİ → ÜRÜNE ÖZEL VİDEO PLANI (JSON prompt).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Ne çözüyor
 * ═══════════════════════════════════════════════════════════════════════════
 * Ürün videosu sekmesinde kullanıcı bir fotoğraf yüklüyor ve o fotoğraf bugüne
 * kadar DOĞRUDAN Seedance'ın `image-to-video` ucuna gidiyordu. Prompt ise
 * kullanıcının yazdığı serbest brief'ten ya da genel bir iskeletten kuruluyordu
 * (`video-prompt.ts` → `DEFAULT_PROMPT_TEMPLATE`) — yani modele "bu fotoğrafı
 * hareket ettir" demenin biraz daha kibar hâli. Sonuç çoğu kez ürünün hafifçe
 * kıpırdadığı, hiçbir reklam fikri taşımayan sekiz saniye oluyordu.
 *
 * Bu dosya araya bir KATMAN koyuyor: fotoğraf önce bir görü modeline (vision)
 * gidiyor, ürün ve içinde bulunduğu ORTAM okunuyor, ve o okumadan ürüne özel
 * bir reklam planı çıkıyor. Plan bir metin değil bir NESNE — çekim çekim
 * kamera, ışık, aksiyon ve süre taşıyor. Prompt o nesneden DERLENİYOR
 * (`compileVideoPlanPrompt`).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 İKİ DİL, TEK NESNE — ve bu bir tutarsızlık değil
 * ═══════════════════════════════════════════════════════════════════════════
 * Planın alanları iki gruba ayrılıyor ve grubun dili SABİT:
 *
 *   KULLANICININ DİLİ → `title`, `rationale`, `onScreenText`, `productFacts`
 *     ve `adAngle` alanları. Bunlar ekranda okunuyor; kullanıcı Türkçe ise
 *     Türkçe olmalı.
 *
 *   İNGİLİZCE → `setting`, `lighting`, `camera`, `shots[].action`,
 *     `styleKeywords`, `negativePrompt`. Bunlar VİDEO MODELİNE gidiyor.
 *     Seedance (ve genel olarak difüzyon tabanlı video modelleri) İngilizce
 *     prompt'ta ölçülebilir biçimde daha isabetli; Türkçe bir "yumuşak yan
 *     ışık" ifadesi modelde karşılık bulmuyor.
 *
 * Kullanıcı planı DÜZENLEYEBİLDİĞİ için (ürün kararı, 20 Ağu 2026) bu ayrım
 * ekranda da görünüyor: teknik alanlar İngilizce duruyor. Alternatif — planı
 * tamamen Türkçe tutup üretimden önce çevirmek — ikinci bir LLM çağrısı ve
 * kullanıcının düzenlediği metnin çeviride kaybolması demekti.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 Plan İSTEMCİDEN geri geliyor — ve şema bu yüzden burada
 * ═══════════════════════════════════════════════════════════════════════════
 * Sahne şablonlarında (`scene-templates.ts`) reçete sunucuda kalır, istemci
 * yalnız kimlik + yuva metni gönderir. Burada tersi geçerli ve gerekçesi
 * farklı: reçete BİZİM değil, kullanıcının ürününe özel üretilmiş ve
 * kullanıcı onu düzenleyebiliyor. Yani üretim isteğiyle birlikte geri gelmek
 * ZORUNDA.
 *
 * Bunun bedeli, prompt'a giren her alanın kullanıcı girdisi sayılması. Üç
 * savunma:
 *   1. `videoPlanSchema` her alanı uzunluk ve sayı olarak sınırlıyor (bir
 *      plan en fazla ~4 KB).
 *   2. Derleyici (`compileVideoPlanPrompt`) metinleri `sanitizePlanText`ten
 *      geçiriyor — kontrol karakterleri ve satır sonları düşüyor, yani
 *      prompt'un içinde sahte bir yönerge bloğu kurulamıyor.
 *   3. Çıktı bir MODELE gidiyor, bir ARACA değil: enjeksiyonun yapabileceği
 *      en kötü şey, kullanıcının kendi workspace'inde kendi kredisiyle başka
 *      bir video üretmek (`video-prompt.ts` ile aynı sınır).
 */

// ═══════════════════════════════════════════════════════════════════════════
// Sabitler
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Analizin kredi bedeli.
 *
 * `COMPETITOR_INSIGHT_CREDITS` (5) ile aynı aile ve aynı gerekçeyle
 * `CREDIT_COST_BY_CREATIVE_TYPE` tablosunda DEĞİL: o tablo `CreativeType`
 * enum'una bağlı ve bir analiz kreatif değil — ortada üretilmiş bir dosya yok.
 *
 * Neden 5 değil 2: rakip raporu 40 reklamı okuyup uzun bir rapor yazıyor,
 * burada tek bir görsel var. Yine de SIFIR değil: bedelsiz bir LLM uçu, elli
 * fotoğrafı arka arkaya yükleyen bir kullanıcıda elli çağrı demek.
 *
 * ⚠️ 20 Ağu 2026'da analiz İKİ sağlayıcı çağrısına bölündü (okuma +
 * konseptler; gerekçe `product-vision.prompt.ts` başında) ve görsel ikisine
 * de gidiyor. Ölçülen maliyet ~$0,04'ten ~$0,09'a çıktı. Kredi bedeli
 * DEĞİŞMEDİ: ürün videosunun kendisi ($0,21…0,47) yanında ikisi de yuvarlama
 * hatası ve 2 kredi zaten caydırıcılık için konmuştu, maliyeti karşılamak
 * için değil.
 */
export const PRODUCT_VISION_CREDITS = 2;

/**
 * Kredi defterindeki referans türü.
 *
 * `render-credit.ts` (worker) bu dizeyi TANIYOR ve iadeyi doğru tabloya
 * yazıyor — `COMPETITOR_INSIGHT_REF` ile birebir aynı desen. Değiştirilirse
 * worker'daki dal da değişmeli, yoksa iade defterde doğru ama analiz
 * satırında "iade edilmedi" görünür.
 */
export const PRODUCT_VISION_REF = 'product_vision_analysis';

/**
 * Kaç konsept üretiliyor.
 *
 * Üç, ürün kararı (20 Ağu 2026): tek konsept beğenilmediğinde ikinci bir
 * analiz (ve ikinci bir kredi) gerektiriyordu; beşten sonrası ise modelin
 * kendini tekrar ettiği yer — dört ve beşinci konseptler ilk üçün kamera
 * hareketi değişmiş kopyaları oluyor.
 */
export const PRODUCT_VISION_CONCEPT_COUNT = 3;

/**
 * Bir plandaki en fazla çekim.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 4 → 6, 20 Ağu 2026 — gerekçesi çürüdüğü için
 * ═══════════════════════════════════════════════════════════════════════════
 * Eski gerekçe şuydu: "Seedance tek bir üretimde sahne kesmesini zaten
 * zorlukla yapıyor; altı çekimlik bir plan modelde kesme değil karmaşa
 * üretiyor." O cümle Seedance'a aitti ve orada kesme METİNDEN çıkarılıyordu
 * ("CUT TO …") — yani her ek çekim, modelin doğru okuma ihtimalini
 * düşüren bir cümleydi.
 *
 * Kling v3'te kesme bir ALAN: `multi_prompt` her elemanı kendi süresi ve
 * kendi prompt'u olan ayrı bir çekim. Ek çekim artık bir belirsizlik değil
 * bir satır. Eleman süresi enum'u da `'1'…'15'` (üst düzey `duration`
 * `'3'`ten başlıyor, eleman `'1'`den) — yani 2 saniyelik çekim sağlayıcının
 * kendi sözleşmesinde geçerli.
 *
 * Altıda duruyor çünkü 15 saniye tavanında (`VIDEO_DURATION.max`) altı çekim
 * zaten çekim başına 2,5 saniye demek; yedincisi kadrajın oturmadığı yere
 * iniyor.
 */
export const VIDEO_PLAN_MAX_SHOTS = 6;

/// Plan metin alanlarının üst sınırları. Prompt'a giren her şey sınırlı:
/// modelin bağlam penceresi değil, DERLENMİŞ prompt'un okunabilirliği için.
export const PLAN_TEXT_MAX = 240;
export const PLAN_TITLE_MAX = 80;
export const PLAN_RATIONALE_MAX = 400;
export const PLAN_LIST_MAX = 6;

/// Müzik brief'ine ayrılan tavan — gerekçe `videoPlanSchema.audioDirection`
/// başlığında. Diğer metin alanlarından (240) geniş, çünkü orada tek bir
/// görsel parça, burada bir tarif duruyor.
export const PLAN_AUDIO_MAX = 320;

/**
 * Çekimin KENDİ dünyasını anlatan ifadenin sınırı — diğer metin alanlarından
 * (240) belirgin dar.
 *
 * Sebep bütçe: bu ifade `multi_prompt` elemanının 512 karakterine giriyor ve
 * orada hareket tarifi (220) ile süreklilik maddeleriyle yarışıyor. 240
 * karakterlik bir mekân cümlesi, tek başına çekimin ne olduğunu anlatan
 * cümleyi dışarı iterdi. 140 karakter "rain-soaked neon crosswalk at night,
 * headlights raking across the puddles" için fazlasıyla yeterli — bir çekimin
 * dünyasını anlatmak için gereken şey uzunluk değil isabet.
 *
 * Sınır aynı zamanda MODELE gidiyor (araç şeması bundan türüyor),
 * yani "kısa tut" bir ricadan çok bir alan sınırı olarak okunuyor.
 */
export const PLAN_SHOT_SETTING_MAX = 140;

/// Kullanıcının analize eklediği serbest not (ör. "kış kampanyası, kar olsun").
export const PRODUCT_VISION_NOTE_MAX = 600;

// ═══════════════════════════════════════════════════════════════════════════
// Analiz katmanı 1 — ÜRÜN KÜNYESİ
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Fotoğraftaki üründen OKUNAN gerçekler.
 *
 * ⚠️ Hepsi modelin ÇIKARIMI, ölçüm değil. Ekranda "tespit" değil "okuma"
 * dilinde sunuluyor: model beyaz bir spor ayakkabıyı "deri" sanabilir ve
 * kullanıcı bunu düzeltebilmeli. Bu yüzden alanlar plana da giriyor —
 * düzeltilen bir malzeme adı doğrudan prompt'u değiştiriyor.
 */
export const productFactsSchema = z.object({
  /// Kullanıcının dilinde, kısa ad: "beyaz deri spor ayakkabı".
  name: z.string().trim().min(1).max(PLAN_TEXT_MAX),
  /**
   * 🔴 AYNI ADIN İNGİLİZCESİ — ve bu alan bir çeviri kolaylığı değil, bir HATA
   * DÜZELTMESİ (20 Ağu 2026, ilk gerçek çağrıda ölçüldü).
   *
   * `name` kullanıcının dilinde ve ekranda okunuyor. Derleyici prompt'un
   * KONUSUNU ondan kuruyordu ve Türkçe bir arayüzde ortaya şu çıkıyordu:
   *
   *   "Commercial product video that starts from the supplied photograph of
   *    Elde köpürtülmüş krem beji katı sabun kalıbı…"
   *
   * Yani cümlenin tek anlam taşıyan parçası, video modelinin anlamadığı bir
   * dilde. Alan boşsa derleyici konuyu HİÇ yazmıyor ("the product") —
   * `name`e düşmek, aynı hatayı sessizce geri getirmek olurdu.
   */
  subjectEn: z.string().trim().max(PLAN_TEXT_MAX).default(''),
  /// Kaba kategori: "ayakkabı", "kozmetik", "içecek". Konsept seçimini
  /// besliyor — bir parfüm şişesiyle bir matkap aynı sahnede çekilmiyor.
  category: z.string().trim().min(1).max(80),
  materials: z.array(z.string().trim().min(1).max(60)).max(PLAN_LIST_MAX).default([]),
  /**
   * Baskın renkler — hex + ad.
   *
   * Hex ZORUNLU çünkü palet buradan türüyor: kullanıcı marka rengi
   * girmediğinde plan `palette`ı ürünün kendi renklerinden kuruyor. Adın da
   * durmasının sebebi prompt: "#1B4D3E" modele bir şey söylemiyor, "deep
   * forest green" söylüyor.
   */
  colors: z
    .array(
      z.object({
        hex: z
          .string()
          .regex(/^#[0-9a-fA-F]{6}$/)
          .transform((value) => value.toLowerCase()),
        /// İNGİLİZCE: doğrudan prompt'a giriyor.
        name: z.string().trim().min(1).max(60),
      }),
    )
    .max(5)
    .default([]),
  /// Form/siluet — "yüksek bilekli, kalın tabanlı".
  shape: z.string().trim().max(PLAN_TEXT_MAX).default(''),
  /**
   * Görselde marka işareti (logo, yazı) görünüyor mu.
   *
   * 🔴 Plan için kritik: görünüyorsa `productContinuity`ye "logoyu deforme
   * etme" maddesi giriyor ve `negativePrompt`a "warped logo" ekleniyor.
   * Video modellerinin en sık bozduğu iki şey metin ve el; ürün fotoğrafında
   * metin çoğu kez markanın kendisi oluyor.
   */
  brandVisible: z.boolean().default(false),
  brandMarks: z.array(z.string().trim().min(1).max(80)).max(3).default([]),
  /// Ürünü diğerlerinden ayıran detaylar — dikiş, kabartma, kapak formu.
  distinctiveDetails: z.array(z.string().trim().min(1).max(120)).max(PLAN_LIST_MAX).default([]),
  /// Ölçek ipucu: "el ayası kadar". `null` = fotoğraftan çıkarılamadı.
  scaleHint: z.string().trim().max(120).nullable().default(null),
});
export type ProductFacts = z.infer<typeof productFactsSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Analiz katmanı 2 — ORTAM / ÇEKİM OKUMASI
// ═══════════════════════════════════════════════════════════════════════════

/// Görselin teknik kalitesi. `poor` olduğunda arayüz uyarı gösteriyor:
/// bulanık bir kaynak görselden çıkan video da bulanık oluyor ve bu, kredi
/// harcandıktan SONRA öğrenilecek bir şey olmamalı.
export const IMAGE_QUALITY_RATINGS = ['excellent', 'good', 'fair', 'poor'] as const;
export type ImageQualityRating = (typeof IMAGE_QUALITY_RATINGS)[number];

/**
 * ÜRÜNÜN İÇİNDE BULUNDUĞU ORTAM — kullanıcının açıkça istediği ikinci okuma.
 *
 * Neden ayrı bir katman: plan bu okumaya göre KARAR DEĞİŞTİRİYOR. Beyaz
 * fonda çekilmiş izole bir ürün, videoda sıfırdan bir sahne kurmayı gerektirir
 * (modelin arka planı uydurması serbest). Mutfak tezgâhında çekilmiş bir ürün
 * ise zaten bir dünyanın içinde — o dünyayı silip yenisini kurmak, ürünün
 * ışığıyla sahnenin ışığı tutmadığı için sahte duruyor.
 *
 * `isolatedProduct` bu ayrımın tek bit'i ve `compileVideoPlanPrompt` onu
 * okuyor.
 */
export const sceneFactsSchema = z.object({
  /// İNGİLİZCE (prompt'a giriyor): "seamless white studio backdrop".
  background: z.string().trim().max(PLAN_TEXT_MAX).default(''),
  surface: z.string().trim().max(PLAN_TEXT_MAX).default(''),
  lighting: z.string().trim().max(PLAN_TEXT_MAX).default(''),
  /// "soft light from upper left" — yön ayrı tutuluyor çünkü videoda ışık
  /// yönünün DEĞİŞMEMESİ gerekiyor; kaynak görselden üretimde model onu
  /// korumalı, yoksa ilk karede ürün başka bir ışıkta beliriyor.
  lightDirection: z.string().trim().max(120).default(''),
  shadow: z.string().trim().max(120).default(''),
  cameraAngle: z.string().trim().max(120).default(''),
  framing: z.string().trim().max(120).default(''),
  /// Görselde ürünün yanında duran nesneler. Videoda ya korunuyor ya
  /// bilerek temizleniyor — plan hangisi olduğunu söylüyor.
  props: z.array(z.string().trim().min(1).max(80)).max(PLAN_LIST_MAX).default([]),
  isolatedProduct: z.boolean().default(true),
  imageQuality: z
    .object({
      rating: z.enum(IMAGE_QUALITY_RATINGS).default('good'),
      /// Kullanıcının dilinde: "arka planda gölge kirli", "ürün çerçeveye
      /// çok yakın". Ekranda uyarı olarak görünüyor.
      issues: z.array(z.string().trim().min(1).max(160)).max(4).default([]),
    })
    .default({ rating: 'good', issues: [] }),
});
export type SceneFacts = z.infer<typeof sceneFactsSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Analiz katmanı 3 — REKLAM YORUMU
// ═══════════════════════════════════════════════════════════════════════════

/**
 * "Bu üründen nasıl bir reklam çıkar" — analizin ürün tarafı.
 *
 * Tamamı KULLANICININ DİLİNDE: ekranda okunuyor ve prompt'a doğrudan
 * girmiyor. Plana dolaylı giriyor — konseptlerin `rationale` alanı bu
 * yorumdan besleniyor.
 */
export const adAngleSchema = z.object({
  audience: z.string().trim().max(PLAN_TEXT_MAX).default(''),
  usageMoment: z.string().trim().max(PLAN_TEXT_MAX).default(''),
  sellingPoints: z.array(z.string().trim().min(1).max(160)).max(5).default([]),
  tone: z.string().trim().max(120).default(''),
  /**
   * ÜRETİMDE BOZULABİLECEK ŞEYLER — modelin kendi zayıflığını bildirdiği yer.
   *
   * "İnce bağcıklar hareket sırasında birbirine karışabilir" gibi. Bu liste
   * ekranda uyarı DEĞİL, `negativePrompt`ın gerekçesi: plan neden o maddeyi
   * yasakladığını böyle açıklıyor.
   */
  risks: z.array(z.string().trim().min(1).max(160)).max(4).default([]),
});
export type AdAngle = z.infer<typeof adAngleSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// VİDEO PLANI — kullanıcının gördüğü ve düzenlediği JSON
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Tek bir çekim.
 *
 * `seconds` bir TALEP değil bir DAĞILIM: Seedance çekim başına süre almıyor,
 * toplam süreyi alıyor. Sayı prompt'a "0:00–0:03" biçiminde zaman damgası
 * olarak giriyor ve modele tempoyu anlatıyor. Toplamın videonun süresine
 * eşit olması `normalizePlanTiming` ile sağlanıyor — kullanıcı süreyi
 * değiştirdiğinde plan yeniden yazılmıyor, ölçekleniyor.
 */
export const videoShotSchema = z.object({
  /**
   * Çekim sırası.
   *
   * ═════════════════════════════════════════════════════════════════════════
   * 🔴 MODELDEN İSTENMİYOR — 20 Ağu 2026, iki ölçülmüş başarısızlıktan sonra
   * ═════════════════════════════════════════════════════════════════════════
   * Alan zorunluydu ve modelin en çok takıldığı yer burasıydı. İki gerçek
   * çağrıda görüldü:
   *
   *     "order": 2.5                          → şema `int` istiyor, analiz düştü
   *     "order": 2.0000001 === 0 ? 3 : 3      → JSON'un içine JS ifadesi
   *
   * İkincisi daha da kötüydü: model o çekim listesini bir JSON METNİ olarak
   * yazmıştı ve ifade metni GEÇERSİZ JSON yapıyordu, yani onarılamıyordu.
   * Üç konseptlik kusursuz bir analiz, tek bir sıra numarası yüzünden
   * tamamen kayboluyordu.
   *
   * Oysa bu alanın modelden gelmesine gerek yok: sıra zaten DİZİNİN
   * KENDİSİNDE ve `normalizePlanTiming` çekimleri 1'den yeniden
   * numaralandırıyor. `default(1)` alanı araç şemasında OPSİYONEL yapıyor
   * (`io: 'input'`), yani model onu hiç yazmayabiliyor; gerçek değeri
   * `parseVisionResponse` dizideki yerinden koyuyor.
   *
   * ⚠️ Alan SİLİNMEDİ: plan kullanıcı tarafından düzenlenebiliyor ve
   * `normalizePlanTiming` sıralamayı buradan okuyor — JSON'da iki çekimin
   * yerini değiştirmenin yolu bu.
   */
  order: z.number().int().min(1).max(VIDEO_PLAN_MAX_SHOTS).default(1),
  seconds: z.number().min(0.5).max(VIDEO_DURATION.ugcMax),
  /// İNGİLİZCE — sahnede ne oluyor.
  action: z.string().trim().min(1).max(PLAN_TEXT_MAX),
  /// İNGİLİZCE — kamera ne yapıyor ("slow push-in on the heel").
  camera: z.string().trim().max(PLAN_TEXT_MAX).default(''),
  /// İNGİLİZCE — neye odaklanılıyor ("stitching detail on the side panel").
  focus: z.string().trim().max(PLAN_TEXT_MAX).default(''),
  /**
   * 🔴 ÇEKİMİN KENDİ DÜNYASI — 20 Ağu 2026'da eklendi, kullanıcı isteğiyle.
   *
   * ═════════════════════════════════════════════════════════════════════════
   * Ne değişti
   * ═════════════════════════════════════════════════════════════════════════
   * Mekân bugüne kadar YALNIZ plan düzeyindeydi (`plan.setting`) ve görü
   * yönergesi bunu bir kural olarak da yazıyordu: "Keep all shots in ONE
   * location with ONE lighting setup." Yani üretilen her plan, tanımı gereği
   * tek mekânda geçen bir üründü — kamera dönüyor, ürün duruyor. Kullanıcının
   * "durağan" dediği şey buydu; istediği ise ürünün arka arkaya BAŞKA BAŞKA
   * ortamlarda görünmesi.
   *
   * O kural Seedance döneminde doğruydu: tek bir `prompt` alanı vardı, mekân
   * değişimi metnin içinde anlatılıyordu ve model iki mekânı iki ürün sanıp
   * ortada başka bir şeye dönüşüyordu. Kling v3'te her çekim AYRI bir prompt
   * (`multi_prompt`) — mekân değişimi artık bir çıkarım değil, elemanın kendi
   * cümlesi.
   *
   * ═════════════════════════════════════════════════════════════════════════
   * BOŞ = ÖNCEKİ ÇEKİMLE AYNI YER
   * ═════════════════════════════════════════════════════════════════════════
   * Alan boş bırakılabiliyor ve bu bir eksiklik değil bir ANLAM: derleyici
   * boş değeri "önceki çekimin dünyası" olarak okuyor (`compileVideoPlanShots`
   * → `shotWorlds`). Böylece tek mekânlı bir konsept hiçbir şey yazmadan eskisi
   * gibi çalışıyor ve iki çekim aynı yerde geçiyorsa mekân tarifi ikinci kez
   * prompt'a girmiyor — 512 karakterlik bütçede bu yer, hareket tarifine
   * kalıyor.
   *
   * ⚠️ DEĞİŞEN ŞEY YALNIZ DÜNYA. Ürünün kendisi (biçim, renk, işaret)
   * `productContinuity` ile HER çekimde sabitleniyor; mekân serbest bırakıldığı
   * için o blok daha da kritik hâle geldi.
   */
  setting: z.string().trim().max(PLAN_SHOT_SETTING_MAX).default(''),
  /**
   * 🔴 ÜRÜN BU ÇEKİMDE KADRAJDA MI — 21 Ağu 2026, kullanıcı isteğiyle.
   *
   * ═════════════════════════════════════════════════════════════════════════
   * Neden bir alan gerekti
   * ═════════════════════════════════════════════════════════════════════════
   * Üçüncü konseptin şekli değişti: ürün artık ortamlar arasında gezmiyor,
   * KENDİ İÇERİĞİNDEN doğuyor — açılış çekimleri ürünün geldiği dünyalar
   * (deniz tuzu spreyinde kum ve tuz, kahvede çekirdek ve duman, deri
   * çantada derinin kendisi) ve ürün ancak son çekimde, bütün hâlde beliriyor.
   *
   * O çekimlerde ürün kadrajda YOK. Derleyici ise her çekime kimlik cümlesi
   * yazıyordu ("Same product, unchanged: the exact object in the reference
   * photo") — yani plan "ürün yok" derken prompt "ürün aynı" diyordu ve
   * çelişkiyi model kendi çözüyordu: ürünü koyuyordu. Alan olmadan bu
   * konsept ÜRETİLEMİYOR, yalnız yazılabiliyordu.
   *
   * ═════════════════════════════════════════════════════════════════════════
   * İKİ ÇEKİM BU ALANI DİNLEMİYOR — `shotProductPresence`
   * ═════════════════════════════════════════════════════════════════════════
   *   • SON çekim her zaman ürünü gösterir. Ürünü göstermeden biten bir ürün
   *     reklamı yok; kapanış zaten "hareket bitiyor, ürün ortada duruyor".
   *   • İLK çekim kaynak fotoğraf varken ürünü gösterir. Sağlayıcı tarafında
   *     `start_image_url` yalnız İLK KAREYİ bağlıyor (gerekçe
   *     `fal-video.provider.ts`) — yani ilk kare zaten ürünün fotoğrafı ve
   *     "ürün kadrajda değil" demek, prompt'un kareyle kavga etmesi demek.
   */
  productInFrame: z.boolean().default(true),
});
export type VideoShot = z.infer<typeof videoShotSchema>;

/**
 * ÜRÜNE ÖZEL VİDEO PLANI — "AI'nin ürettiği JSON prompt" bu.
 *
 * Bir konsept = bir plan. Analiz üç tane döndürüyor, kullanıcı birini seçiyor
 * ve seçilen plan (düzenlenmiş hâliyle) üretim isteğine giriyor.
 */
export const videoPlanSchema = z.object({
  /**
   * Konsept kimliği — slug.
   *
   * Modelden geliyor ve tekilliği garanti değil; `parseVisionResponse` çakışma
   * hâlinde sıra numarası ekliyor. Arayüz kart seçimini bununla tutuyor, bu
   * yüzden bir kimliğin iki karta düşmesi "birini seçince ikisi de seçili"
   * demekti.
   */
  id: z.string().trim().min(1).max(60),
  /// Kullanıcının dilinde konsept adı: "Karanlıktan doğan siluet".
  title: z.string().trim().min(1).max(PLAN_TITLE_MAX),
  /// Kullanıcının dilinde: bu konsept neden BU ürüne uygun.
  rationale: z.string().trim().max(PLAN_RATIONALE_MAX).default(''),
  /// İNGİLİZCE stil anahtarları: "cinematic", "high contrast", "macro".
  styleKeywords: z.array(z.string().trim().min(1).max(60)).max(PLAN_LIST_MAX).default([]),
  setting: z
    .object({
      location: z.string().trim().max(PLAN_TEXT_MAX).default(''),
      surface: z.string().trim().max(PLAN_TEXT_MAX).default(''),
      background: z.string().trim().max(PLAN_TEXT_MAX).default(''),
      props: z.array(z.string().trim().min(1).max(80)).max(PLAN_LIST_MAX).default([]),
    })
    .default({ location: '', surface: '', background: '', props: [] }),
  lighting: z
    .object({
      key: z.string().trim().max(PLAN_TEXT_MAX).default(''),
      fill: z.string().trim().max(PLAN_TEXT_MAX).default(''),
      accent: z.string().trim().max(PLAN_TEXT_MAX).default(''),
      mood: z.string().trim().max(120).default(''),
    })
    .default({ key: '', fill: '', accent: '', mood: '' }),
  camera: z
    .object({
      lens: z.string().trim().max(120).default(''),
      movement: z.string().trim().max(PLAN_TEXT_MAX).default(''),
      angle: z.string().trim().max(120).default(''),
    })
    .default({ lens: '', movement: '', angle: '' }),
  shots: z.array(videoShotSchema).min(1).max(VIDEO_PLAN_MAX_SHOTS),
  palette: z
    .object({
      primary: z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/)
        .nullable()
        .default(null),
      accent: z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/)
        .nullable()
        .default(null),
      background: z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/)
        .nullable()
        .default(null),
    })
    .default({ primary: null, accent: null, background: null }),
  motion: z
    .object({
      pacing: z.string().trim().max(120).default(''),
      transition: z.string().trim().max(120).default(''),
    })
    .default({ pacing: '', transition: '' }),
  /**
   * 🔴 ÜRÜNÜN DEĞİŞMEMESİ GEREKEN YANLARI — planın en değerli alanı.
   *
   * Video modelleri kaynak görselden üretirken ürünü "yeniden yorumluyor":
   * ayakkabının taban rengi kayıyor, şişenin etiketi başka bir yazıya
   * dönüşüyor. Bu maddeler prompt'un BAŞINA — konu cümlesinin hemen ardına —
   * bir süreklilik bloğu olarak giriyor (`compileVideoPlanPrompt`).
   */
  productContinuity: z.array(z.string().trim().min(1).max(160)).max(PLAN_LIST_MAX).default([]),
  /// İNGİLİZCE — plana özel yasaklar. Genel liste (`video-prompt.ts`) bunun
  /// üstüne EKLENİYOR, yerine geçmiyor.
  negativePrompt: z.array(z.string().trim().min(1).max(80)).max(10).default([]),
  /**
   * Ekran metni önerisi — kullanıcının dilinde.
   *
   * ⚠️ Prompt'a GİRMİYOR ve girmemeli: video modelleri metni bozuyor
   * (`DEFAULT_NEGATIVE_PROMPT` zaten "text overlays" yasaklıyor). Bu alan
   * kullanıcının videoyu yayımlarken kullanacağı başlık/CTA fikri — üretimin
   * değil, reklamın malzemesi.
   */
  onScreenText: z
    .object({
      headline: z.string().trim().max(80).default(''),
      cta: z.string().trim().max(40).default(''),
    })
    .nullable()
    .default(null),
  /**
   * İNGİLİZCE MÜZİK BRIEF'İ. `generate_audio` kapalıyken bile duruyor:
   * kullanıcı sesi açarsa aynı plan onu da anlatıyor.
   *
   * 🔴 SINIRI 240 DEĞİL 320 — ve fark ölçülmüş bir başarısızlıktan geliyor
   * (20 Ağu 2026). Alan uzun süre boş bir vaatti (prompt'a hiç girmiyordu);
   * bağlanıp görü yönergesi gerçek bir müzik brief'i istemeye başlayınca
   * model doğru olanı yazdı ve 240'ı aştı:
   *
   *     "Sun-drenched indie surf-pop instrumental: reverby single-coil guitar
   *      riff, tight live drums with rimshot, warm bass, ~124 BPM. Handclaps
   *      enter on cut two, energy…"
   *
   * Tür + enstrüman + tempo + enerji yayı + çözülüş, 240 karakterde
   * söylenmiyor. Diğer metin alanları `PLAN_TEXT_MAX`ta KALIYOR: onlar tek bir
   * görsel parça anlatıyor, bu bir tarif.
   */
  audioDirection: z.string().trim().max(PLAN_AUDIO_MAX).nullable().default(null),
});
export type VideoPlan = z.infer<typeof videoPlanSchema>;
export type VideoPlanInput = z.input<typeof videoPlanSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Analizin tamamı
// ═══════════════════════════════════════════════════════════════════════════

export const productVisionResultSchema = z.object({
  product: productFactsSchema,
  scene: sceneFactsSchema,
  angle: adAngleSchema,
  concepts: z.array(videoPlanSchema).min(1).max(PRODUCT_VISION_CONCEPT_COUNT),
  /// Modelin bir cümlelik özeti — kullanıcının dilinde. Kart başlığında
  /// duruyor; kullanıcı ayrıntıya inmeden "model neyi gördü" sorusunu
  /// cevaplıyor.
  summary: z.string().trim().max(PLAN_RATIONALE_MAX).default(''),
});
export type ProductVisionResult = z.infer<typeof productVisionResultSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Uç sözleşmesi
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Analiz isteği.
 *
 * Kaynak `creativeGenerateSchema` ile aynı ikilik: yüklenmiş dosya
 * (`sourceAssetKey`) ya da katalog ürünü (`productId`), TAM OLARAK BİRİ. Aynı
 * `refine` burada da var ve gerekçesi aynı — iki taban gönderildiğinde
 * hangisinin analiz edildiği ancak sonuca bakarak anlaşılırdı.
 */
export const productVisionRequestSchema = z
  .object({
    sourceAssetKey: z.string().max(400).optional(),
    productId: z.uuid().optional(),
    /**
     * ÜRETİM AYARLARI — plan bunlara göre kuruluyor.
     *
     * 🔴 Analiz süreyi ve oranı BİLMEK ZORUNDA: 9:16 dikey bir videoda çekim
     * kompozisyonu 16:9'dakinden başka, ve 6 saniyeye dört çekim sığmıyor.
     * Ayarları sormadan üretilen bir plan, kullanıcı oranı değiştirdiğinde
     * sessizce yanlış hâle gelirdi.
     *
     * Kullanıcı ayarları sonradan değiştirirse plan YENİDEN ÜRETİLMİYOR,
     * `normalizePlanTiming` ile ölçekleniyor — yeni bir kredi yakmıyor.
     */
    aspectRatio: videoAspectRatioSchema.default('9:16'),
    /// `videoParamsSchema` ile AYNI varsayılan (1080p) — ikisi ayrışırsa
    /// analiz bir çözünürlük için plan yazar, üretim başkasında çalışır.
    resolution: videoResolutionSchema.default('1080p'),
    durationSeconds: z
      .number()
      .int()
      .min(VIDEO_DURATION.min)
      .max(VIDEO_DURATION.max)
      .default(VIDEO_DURATION.productDefault),
    /// Arayüzün dili — `title`/`rationale`/`summary` bu dilde dönüyor
    /// (29 Eyl 2026: 13 dil — `localeSchema`).
    language: localeSchema.default('tr'),
    /// Marka bilgisi — varsa plana giriyor, yoksa model ürünün kendi
    /// renklerinden türetiyor.
    brandTone: z.string().trim().max(200).optional(),
    brandColor: z
      .string()
      .regex(/^#[0-9a-fA-F]{6}$/)
      .optional(),
    /// Kullanıcının serbest notu: "kış kampanyası", "genç kitle".
    note: z.string().trim().max(PRODUCT_VISION_NOTE_MAX).optional(),
    /**
     * ÖNBELLEĞİ ATLA — "yeniden analiz et".
     *
     * Varsayılan `false` ve bu, kredinin korunduğu yer: aynı fotoğraf için
     * başarılı bir analiz varsa uç onu DÖNDÜRÜYOR, yeni bir iş açmıyor.
     * Arayüz yükleme sonrası analizi kendiliğinden tetiklediği için bu kontrol
     * olmasa her sayfa yenilemesi 2 kredi yakardı.
     *
     * `true` yalnız kullanıcı açıkça istediğinde (not yazıp "yeniden analiz
     * et" dediğinde) gönderiliyor — o zaman ortada yeni bir girdi var ve eski
     * sonucu döndürmek yanlış olurdu.
     */
    force: z.boolean().default(false),
  })
  .refine((input) => Boolean(input.sourceAssetKey) !== Boolean(input.productId), {
    message: 'sourceAssetKey ya da productId — tam olarak biri gerekli.',
    path: ['sourceAssetKey'],
  });
export type ProductVisionRequestInput = z.infer<typeof productVisionRequestSchema>;

/**
 * Analiz satırı — liste ve yoklama yanıtı.
 *
 * `CompetitorInsightRow` ile aynı şekil ve aynı gerekçe: durum + sonuç + hata
 * tek satırda, arayüz tek uçtan yokluyor.
 */
export interface ProductVisionRow {
  id: string;
  status: 'QUEUED' | 'RUNNING' | 'SUCCEEDED' | 'FAILED' | 'CANCELED';
  /// Hangi kaynaktan üretildi — arayüz "bu fotoğrafın analizi var mı"
  /// sorusunu bununla cevaplıyor.
  sourceAssetKey: string | null;
  productId: string | null;
  aspectRatio: string;
  durationSeconds: number;
  result: ProductVisionResult | null;
  /**
   * Hata ANAHTARI — ham sağlayıcı metni değil.
   *
   * `saglayici-hatasi-kullaniciya-gosterilmez` kuralı: ayrıntı logda kalıyor,
   * ekrana i18n anahtarı çıkıyor.
   */
  errorCode: string | null;
  createdAt: string;
  finishedAt: string | null;
}

/// Analiz açma yanıtı — iş kuyruğa girdi.
export interface ProductVisionStartResponse {
  analysis: ProductVisionRow;
  /// Rezerve edilen kredi; arayüz bakiye çipini tazeliyor.
  reservedCredits: number;
}

/**
 * ANALİZİN BAŞARISIZLIK SEBEPLERİ — ekrana çıkan i18n anahtarının gövdesi.
 *
 * 🔴 Ham sağlayıcı metni kullanıcıya GÖSTERİLMİYOR (`saglayici-hatasi-
 * kullaniciya-gosterilmez` kuralı): worker ayrıntıyı `error` kolonuna ve loga
 * yazıyor, ekrana yalnız bu kodlardan biri çıkıyor. Arayüz onu
 * `dashboard.creatives.vision.error.<code>` anahtarıyla çeviriyor.
 *
 * Liste burada çünkü İKİ TARAF da okuyor: worker yazıyor, arayüz çeviriyor.
 * Elle kopyalanan bir liste, çevirisi olmayan bir kodun ekranda ham metin
 * olarak görünmesi demekti (`sunucu-anahtar-gonderir-ekran-cevirir`).
 */
export const PRODUCT_VISION_ERROR_CODES = [
  /// `ANTHROPIC_API_KEY` yok ya da reddedildi (401/403).
  'PROVIDER_NOT_CONFIGURED',
  /// Görsel sağlayıcının kabul ettiği boyutu aşıyor. Kullanıcı eylemi var:
  /// daha küçük bir fotoğraf yükleyebilir — bu yüzden ayrı kod.
  'IMAGE_TOO_LARGE',
  /// Görsel okunamadı: S3'te yok, indirilemedi ya da desteklenmeyen biçim.
  'IMAGE_UNREADABLE',
  /// Model içerik politikası gereği reddetti. Yeniden denemek aynı cevabı
  /// alır — kredi iade ediliyor.
  'MODEL_REFUSED',
  /// Model yanıt verdi ama çıktı sözleşmeye uymuyor.
  'BAD_FORMAT',
  /// Sınıflandırılamayan terminal hata.
  'UNKNOWN',
] as const;
export type ProductVisionErrorCode = (typeof PRODUCT_VISION_ERROR_CODES)[number];

/// Analiz yoklama aralığı (ms). Arayüz kendi sayısını seçmesin diye burada.
/// Video render'ının 3 sn'sinden hızlı: analiz 10–25 saniyede bitiyor ve
/// 3 saniyelik bir tur, biten bir işi 3 saniye gizli tutardı.
export const PRODUCT_VISION_POLL_MS = 2000;

// ═══════════════════════════════════════════════════════════════════════════
// Zamanlama — plan süreye uydurulur, yeniden üretilmez
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Çekim sürelerini videonun gerçek süresine ÖLÇEKLER.
 *
 * Model 8 saniye için plan yazdı, kullanıcı süreyi 12'ye çıkardı: çekimler
 * oranları korunarak uzuyor. Alternatif — planı yeniden üretmek — ikinci bir
 * kredi ve kullanıcının düzenlediği metinlerin kaybı demekti.
 *
 * Son çekim ARTIĞI TOPLUYOR: üçe bölünmeyen bir süre (7 sn / 3 çekim) her
 * çekimi ayrı ayrı yuvarlarsa toplam 6,9 ya da 7,2 çıkar ve zaman damgaları
 * videonun sonunu aşar.
 */
export function normalizePlanTiming(plan: VideoPlan, durationSeconds: number): VideoPlan {
  const shots = [...plan.shots].sort((a, b) => a.order - b.order);
  const total = shots.reduce((sum, shot) => sum + shot.seconds, 0);
  if (shots.length === 0 || total <= 0) return plan;

  const scale = durationSeconds / total;
  let used = 0;
  const scaled = shots.map((shot, index) => {
    if (index === shots.length - 1) {
      return { ...shot, order: index + 1, seconds: round1(Math.max(0.5, durationSeconds - used)) };
    }
    const seconds = round1(Math.max(0.5, shot.seconds * scale));
    used += seconds;
    return { ...shot, order: index + 1, seconds };
  });

  return { ...plan, shots: scaled };
}

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

// ═══════════════════════════════════════════════════════════════════════════
// DERLEYİCİ — plan → sağlayıcı prompt'u
// ═══════════════════════════════════════════════════════════════════════════

export interface CompiledVideoPrompt {
  prompt: string;
  negativePrompt: string;
}

/// Tek bir çekim — sağlayıcının çok çekimli girdisine giden eleman.
export interface CompiledVideoShot {
  /// TAM SANİYE. Sağlayıcı kesir kabul etmiyor (enum: '1'…'15').
  seconds: number;
  prompt: string;
}

export interface CompiledVideoShotList {
  shots: CompiledVideoShot[];
  negativePrompt: string;
}

/**
 * ÇEKİM BAŞINA 512 KARAKTER — Kling `multi_prompt` elemanının GERÇEK sınırı.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 BU SAYI ŞEMADA YAZMIYOR. Ölçüldü — ve pahalıya öğrenildi.
 * ═══════════════════════════════════════════════════════════════════════════
 * `KlingV3MultiPromptElement.prompt` şemada şudur:
 *
 *     { "title": "Prompt", "description": "The prompt for this shot.",
 *       "type": "string" }
 *
 * `maxLength` YOK. Üst düzey `prompt` alanında var ve 2500; ilk sürümde bu
 * sayı elemana da uygulandı. 20 Ağu 2026'daki ilk gerçek üretim 422 ile
 * düştü:
 *
 *     multi_prompt[0].prompt — "Prompt must not exceed 512 characters."
 *
 * Üretilen çekimler 1032 / 697 / 727 karakterdi. Kullanıcının 71 kredisi
 * rezerve edilip iade edildi.
 *
 * ⚠️ DERS: şemayı okumak alanın VARLIĞINI doğruluyor, SINIRINI değil.
 * `negative_prompt` olayında şemada olmayan bir alan gönderiliyordu ve fal
 * sessizce atıyordu; burada şemada olmayan bir sınır vardı ve fal 422
 * veriyordu. İkisi de "şema tam sözleşme değil" diyor — sınırlar ancak
 * gerçek bir çağrıyla öğreniliyor.
 *
 * 🔴 Bu yüzden derleyici sınırı SONDAN KIRPMIYOR, BÜTÇE OLARAK DAĞITIYOR.
 * Kırpmak, çekimin ne olduğunu anlatan cümleyi (metnin sonundaki hareket)
 * kesip yerine marka rengi listesi bırakırdı.
 */
const SHOT_PROMPT_MAX_CHARS = 512;

/**
 * 🔴 `@Element1` SAYIMA 9 DEĞİL 15 KARAKTER OLARAK GİRİYOR — ölçüldü, 20 Ağu 2026.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Nasıl ölçüldü ve neden tahmin edilemezdi
 * ═══════════════════════════════════════════════════════════════════════════
 * Eleman referansı bağlandıktan sonraki ilk gerçek üretim 422 ile düştü:
 *
 *     multiPrompt[1].prompt: size must be between 0 and 512
 *
 * Oysa o çekim 508 KARAKTERDİ. Sınırın karakter mi bayt mı sayıldığı sorusu
 * ücretsiz doğrulama çağrılarıyla ölçüldü — istek kuyruğa giriyor, üst kaynak
 * doğrulaması düşürüyor, video ÜRETİLMİYOR (`inference_time` < 1 sn, ücret
 * yok). Ölçülen sınır çifti:
 *
 *     512 karakter, `@Element1` YOK   → geçiyor
 *     506 karakter, `@Element1` VAR   → geçiyor
 *     507 karakter, `@Element1` VAR   → DÜŞÜYOR
 *
 * Yani jeton üst kaynakta bir iç kimliğe genişletiliyor ve sayıma 6 karakter
 * FAZLADAN giriyor. Ne fal'ın şeması ne de hata mesajı bunu söylüyor; şema
 * `maxLength` olarak 512'yi doğruluyor ve pydantic katmanı 512 karakteri
 * KABUL EDİYOR — reddeden, arkasındaki Kling API'si.
 *
 * ⚠️ Jeton kaç kez geçerse o kadar pay ayrılmalı. Derleyici çekim başına BİR
 * kez anıyor; sayı değişirse burası da değişir.
 */
const ELEMENT_REF_OVERHEAD = 6;

/**
 * Ölçülen sınırın ALTINDA bırakılan pay.
 *
 * 506 geçiyor, 507 düşüyor — yani 506'da üretmek doğru ama sıfır paylı. Jetonun
 * genişlediği iç kimliğin uzunluğunun her istekte aynı olduğunu VARSAYIYORUZ ve
 * bu varsayımın yanlış çıkması, kullanıcının bir üretiminin 422 ile düşmesi
 * demek (bir kez yaşandı: iki iş düştü, krediler iade edildi). İki karakter,
 * sahne tarifinden fark edilmeyecek kadar az; bir üretimin düşmesi ise
 * fark ediliyor.
 */
const SHOT_PROMPT_SAFETY_MARGIN = 2;

/// Hareket tarifine ayrılan tavan. Çekimi ÇEKİM yapan cümle bu; bütçe önce
/// buna ayrılıyor, süreklilik ve ortam kalanı paylaşıyor.
const SHOT_DETAIL_MAX_CHARS = 220;

/**
 * Hareket tarifinin TABANI — tavanın altındaki pay artık dinamik.
 *
 * Uzun bir kesme cümlesi (yeni mekân + kimlik kuyruğu) tavanı zorladığında
 * hareket kısalıyor, ürünün kimliği kısalmıyor. Ama sıfıra da inemez: hareketi
 * olmayan bir çekim, kullanıcının "sıkıcı" dediği tek uzun kadrajın ta kendisi.
 * 110 karakter bir eylem + bir kamera hareketi için yeterli ("the bottle spins
 * on the wet stone; camera whips past at lens height").
 */
const SHOT_DETAIL_MIN_CHARS = 110;

/// Kapanış çekiminde hareket tarifinin tabanı. Orada bütçeyi üç şey birden
/// paylaşıyor (kimlik, müzik, kapanış) ve kapanış cümlesi hareketin nasıl
/// bittiğini kendisi söylüyor.
const SHOT_CLOSING_DETAIL_MIN_CHARS = 80;

/**
 * Tek bir süreklilik maddesine ayrılan tavan.
 *
 * Şema 160'a kadar izin veriyor (`productContinuity`) ve model bazen tam
 * sınırda yazıyor. 512'lik bir elemanda iki uzun madde, hareket tarifiyle
 * birlikte bütçenin tamamı demek — kırpılan madde, hiç yazılmayan maddeden
 * iyi: "cream off-white base with warm grey suede overlays on every" cümlesi
 * de ürünü kilitliyor.
 */
const CONTINUITY_ITEM_MAX_CHARS = 120;

/**
 * Kesme sonrası yeni mekân cümlesine ayrılan tavan.
 *
 * Şema alanı 140 (`PLAN_SHOT_SETTING_MAX`) ama burada 110'a iniyor ve fark
 * kasıtlı: 512'lik elemanda hareket zaten 220 alıyor, açılış kalıbı ~50 —
 * mekâna 140 verilse geriye süreklilik için 100 karakter kalır ve ürünün
 * kimliğini koruyan maddeler ikinciden sonra düşerdi. Mekân bir sahne
 * KURULUMU, süreklilik ise ürünün kendisi; daralmada mekânın kısalması,
 * ürünün bozulmasından iyidir.
 */
const SHOT_WORLD_MAX_CHARS = 90;

/**
 * ÇEKİM BAŞINA DÜNYA — boş `setting` "öncekiyle aynı yer" demek.
 *
 * Tek yerde çözülüyor çünkü İKİ derleyici de aynı cevabı istiyor
 * (`compileVideoPlanShots` kesme cümlesi için, `compileVideoPlanPrompt` çekim
 * listesi için) ve iki kopya, bir gün birinin devralma kuralını değiştirip
 * diğerinin değiştirmemesi demekti — o durumda tek metinli yolla çok çekimli
 * yol aynı planı farklı mekânlarda anlatırdı.
 *
 * Başlangıç değeri plan düzeyindeki mekân: hiçbir çekim kendi `setting`ini
 * yazmamışsa sonuç eski davranışın birebir aynısı (tek mekân, hiçbir kesme
 * cümlesi).
 */
export function shotWorlds(plan: VideoPlan): string[] {
  let current = clean(plan.setting.location);
  return plan.shots.map((shot) => {
    const own = clean(shot.setting);
    if (own.length > 0) current = own;
    return current;
  });
}

/**
 * ÇEKİM BAŞINA "ÜRÜN KADRAJDA MI" — planın dediği değil, UYGULANAN değer.
 *
 * `videoShotSchema.productInFrame` kullanıcının/modelin niyeti; burası iki
 * yerde onu EZİYOR ve ikisinin de gerekçesi alanın başlığında:
 *   • son çekim her zaman ürünü gösterir,
 *   • kaynak fotoğraf varken ilk çekim de gösterir (`start_image_url` ilk
 *     kareyi ürünün fotoğrafına bağlıyor).
 *
 * `shotWorlds` ile aynı sebeple TEK yerde: iki derleyici de aynı cevabı
 * istiyor ve ekranda görünen "ürün yok" işareti ile modele giden cümle
 * ayrışmamalı.
 */
export function shotProductPresence(plan: VideoPlan, hasSourceImage: boolean): boolean[] {
  const last = plan.shots.length - 1;
  return plan.shots.map((shot, index) => {
    if (index === last) return true;
    if (index === 0 && hasSourceImage) return true;
    return shot.productInFrame;
  });
}

/**
 * PLANI ÇEKİM ÇEKİM DERLER — Kling v3'ün `multi_prompt` girdisi için.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden `compileVideoPlanPrompt`ten AYRI bir fonksiyon
 * ═══════════════════════════════════════════════════════════════════════════
 * O fonksiyon planı TEK bir metne eriyor ve kesmeleri metnin içinde
 * ("CUT TO") anlatıyor — Seedance'ta başka yol yoktu, tek bir `prompt` alanı
 * vardı. Kling v3 bunu bir ALAN olarak sunuyor: `multi_prompt`, her elemanı
 * `{duration, prompt}` olan bir dizi, "videoyu birden çok çekime böler".
 *
 * ⚠️ İKİSİ BİRDEN GÖNDERİLEMİYOR: "Either prompt or multi_prompt must be
 * provided, but not both." Yani paylaşılan bağlamı (ürün künyesi, mekân,
 * ışık) üst düzey `prompt`a koyup elemanları kısa tutmak MÜMKÜN DEĞİL.
 * Her çekim kendi bağlamını taşımak zorunda ve toplam 512 karakter.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * BÜTÇE SIRASI — neyin önce yer aldığı bir tasarım kararı
 * ═══════════════════════════════════════════════════════════════════════════
 *   1. HAREKET (`detail`). Çekimi diğerlerinden ayıran tek şey. Yeri
 *      ÖNCEDEN ayrılıyor; taşarsa kendi içinde kırpılıyor.
 *   2. KİMLİK. İlk çekimde konu cümlesi, sonrakilerde "aynı ürün ve ortam".
 *      Ölçülen başarısızlık ürünün kesmeler arasında değişmesiydi.
 *   3. SÜREKLİLİK maddeleri — sığdığı kadar, sırayla. Marka işareti önce
 *      (modelin en sık bozduğu şey metin).
 *   4. ORTAM ve stil — yalnız İLK çekimde ve yalnız yer kalırsa. Sonraki
 *      çekimler zaten "aynı ortam" diyor.
 *
 * Yani daralma olduğunda kaybedilen şey en alttan başlıyor: sahne süsü
 * gider, hareket ve ürün kimliği kalır.
 */
export function compileVideoPlanShots(
  plan: VideoPlan,
  options: CompileVideoPlanOptions,
): CompiledVideoShotList {
  const timed = normalizePlanTiming(plan, options.durationSeconds);
  const seconds = ensureClosingBeat(
    wholeSeconds(
      timed.shots.map((shot) => shot.seconds),
      options.durationSeconds,
    ),
  );

  const subject = clean(options.product?.subjectEn ?? '');
  /* Maddeler BURADA kırpılıyor, `continuityItems` içinde değil: tek metinli
     derleyicinin bütçesi 2500 ve orada kırpmak, kırpılmasına gerek olmayan bir
     cümleyi yarım bırakmak olurdu. */
  const continuity = continuityItems(timed, options).map((item) =>
    capText(item, CONTINUITY_ITEM_MAX_CHARS),
  );
  /* Çekim başına dünya. Boş `setting` "öncekiyle aynı yer" demek —
     çözümlemesi `shotWorlds`ta, gerekçesi `videoShotSchema.setting`te. */
  const worlds = shotWorlds(timed);
  /* Çekim başına "ürün kadrajda mı". Ürünün kendi içeriğinden doğduğu
     konseptte açılış çekimlerinde ürün YOK — gerekçe
     `videoShotSchema.productInFrame`. */
  const present = shotProductPresence(timed, options.hasSourceImage);

  /* ORTAM — yalnız ilk çekimde ve yalnız yer kalırsa. Kaynak görsel varken
     zaten kısalıyor (model kareye demirliyor); burada ayrıca bütçeye tabi.

     🔴 Mekân artık plan düzeyinden değil İLK ÇEKİMİN dünyasından okunuyor.
     Ortam serisi olan bir konseptte `plan.setting.location` yalnız açılış
     dünyasını anlatıyor ve ilk çekim kendi `setting`ini yazmışsa o daha
     kesin; `worlds[0]` ikisinin doğru olanını zaten seçiyor. */
  const setting = (
    options.hasSourceImage ? [worlds[0] ?? ''] : [worlds[0] ?? '', clean(timed.setting.surface)]
  ).filter((value) => value.length > 0);
  const key = clean(timed.lighting.key);
  const styleLine =
    timed.styleKeywords.length > 0 ? `Style: ${joinList(timed.styleKeywords)}.` : '';
  /* Müzik HER çekimde tekrarlanıyor. `multi_prompt` elemanları arasında bağ
     yok — model her elemanı kendi başına kompoze ediyor ve müziği yalnız ilk
     çekime yazmak, ikinci kesmeden sonra ortam efektine dönmek demekti (aynı
     gerekçe süreklilik maddelerinde de geçerli). Cümle kısa tutuluyor: tarif
     bütçede hareketin yerini almamalı. */
  const music = musicBrief(timed, options);
  const musicLine = music ? `Music: ${capText(music, SHOT_MUSIC_MAX_CHARS)}, no vocals.` : '';
  const setLine = setting.length > 0 ? `Set: ${setting.join(', ')}.` : '';
  const lightLine = key ? `Light: ${key}.` : '';

  /**
   * 🔴 İLK ÇEKİMİN MEKÂNI ORTAM SERİSİNDE GARANTİLİ — ölçülmüş bir boşluk.
   *
   * Bütçe sırası şuydu: stil garantili, sonra süreklilik, kalırsa mekân ve
   * ışık. Tek mekânlı planda doğruydu — mekân zaten kaynak fotoğrafta.
   *
   * Ortam serisinde değil: 20 Ağu 2026'da yeni yönergeyle üretilen üç
   * konseptin İKİSİNDE ilk çekimin `Set:` satırı bütçeye kurban gitti.
   * Sonuç tuhaf bir asimetri — 2.…5. çekimler dünyalarını adıyla söylüyor
   * ("Hard cut to a new place: …"), 1. çekim söylemiyor. Yani serinin tek
   * KURULMAYAN dünyası açılış dünyası oluyordu.
   *
   * Garanti YALNIZ seride veriliyor: tek mekânlı planda eski sıra aynen
   * duruyor ve süreklilik bir satır daha fazla yer alıyor.
   */
  const montage = worlds.some((world, index) => index > 0 && world !== worlds[index - 1]);

  /* Efektif tavan: jeton anılıyorsa sınır 512 değil 506 (yukarıdaki ölçüm). */
  const promptLimit =
    SHOT_PROMPT_MAX_CHARS -
    (clean(options.productRef ?? '') ? ELEMENT_REF_OVERHEAD + SHOT_PROMPT_SAFETY_MARGIN : 0);

  const shots = timed.shots.map((shot, index) => {
    /* Kimlik cümlesi. İlk çekimde konuyu tanıtıyor, sonrakiler ona bağlanıyor
       — "aynı" demek, modelin yeni bir ürün ya da oda uydurmasını engelleyen
       en kısa cümle. Konu adı sonraki çekimlerde TEKRARLANMIYOR: 512
       bütçesinde uzun bir isim tamlaması, iki süreklilik maddesi kadar yer
       kaplıyor ve `start_image_url` zaten ürünü gösteriyor.

       🔴 ÜÇÜNCÜ HÂL — DÜNYA DEĞİŞİMİ (20 Ağu 2026).
       Çekimin kendi `setting`i öncekinden farklıysa cümle "aynı yer" DEMİYOR,
       tersini söylüyor: yeni yer adıyla yazılıyor ve ürünün değişmediği ayrıca
       belirtiliyor. İkisini aynı cümlede tutmak bilinçli — modele verilen tek
       cümlede "değişen ne, değişmeyen ne" birlikte duruyor; iki ayrı cümlede
       ikincisi bütçe daralınca düşen ilk şey olurdu.

       Mekân ifadesi burada AYRICA kırpılıyor (`SHOT_WORLD_MAX_CHARS`):
       şemadaki 140 karakterin tamamı, hareket tarifiyle birlikte süreklilik
       maddelerini bütçe dışına iterdi. */
    const world = worlds[index] ?? '';
    const worldChanged = index > 0 && world.length > 0 && world !== worlds[index - 1];
    /* 🔴 ÜRÜN HER ÇEKİMDE ELEMANLA ANILIYOR — 20 Ağu 2026.
       `@Element1` yalnız ANILDIĞI çekimde devreye giriyor (sağlayıcının kendi
       tarifi). İlk çekime yazıp geçmek, tam da bozulmanın yaşandığı yeri —
       kesmeden sonraki çekimleri — referanssız bırakırdı. */
    const ref = clean(options.productRef ?? '');
    /* Kimlik kuyruğu HER çekimde aynı: "aynı ürün" demek yetmiyordu, çünkü
       model "aynı TÜR ürün" diye okuyup benzerini çiziyordu (ölçüldü —
       ikinci çekimde taban rengi kayıyor, üçüncüde logo başka bir yazıya
       dönüşüyor). Cümle artık FOTOĞRAFA atıf yapıyor ve eleman jetonu varsa
       o atıf sağlayıcı için de çözülebilir hâle geliyor. */
    /* 🔴 KISA TUTULUYOR — 20 Ağu 2026, müzik ve kapanış cümlesi eklenince.
       Uzun hâli ("identical to the reference photo — same shape, colours and
       markings", 103 karakter) tek başına doğruydu ama 506'lık bütçede son
       çekimde kapanış cümlesini dışarı itiyordu. Kilidin ağır işini zaten
       eleman referansı ve `Keep exactly` listesi yapıyor; bu cümlenin işi
       modele "yeni bir ürün çizme" demek ve bunu 76 karakterde de söylüyor. */
    const same = ref
      ? `${ref}, the exact object in the reference photo`
      : 'the exact object in the reference photo, not a lookalike';
    /* 🔴 ÜRÜNSÜZ ÇEKİM — 21 Ağu 2026.
       Ürünün kendi içeriğinden doğduğu konseptte açılış çekimleri ürünün
       DÜNYASINI gösteriyor, ürünü değil. İki şey birden gerekiyor:
       kimlik cümlesi yazılmamalı (yoksa "ürün aynı" der ve model ürünü
       koyar) ve `@Element1` ANILMAMALI — jeton yalnız anıldığı çekimde
       devreye giriyor, yani anmamak elemanı o çekimde kapatmanın yolu.

       Ürünün geri geldiği çekim ayrıca işaretleniyor: "yine aynı ürün"
       demek yanlış olurdu (önceki çekimde ürün yoktu), doğru cümle
       BELİRME cümlesi — konseptin fikri de tam olarak o an. */
    const inFrame = present[index] ?? true;
    const arriving = inFrame && index > 0 && !(present[index - 1] ?? true);
    const opening = !inFrame
      ? `${index === 0 ? 'Opening shot' : 'Hard cut'}: ${capText(world, SHOT_WORLD_MAX_CHARS)}. No product in frame — this shot is only the world it comes from.`
      : arriving
        ? /* 🔴 "ZATEN BİTMİŞ" DEĞİL "BİRLEŞİYOR" — 21 Ağu 2026. İlk hâl
             ("whole and already finished") kesme dili konuşuyordu ve üretilen
             videoda birleşme hiç olmadı: elemanlar gösterildi, sert kesme,
             ürün hazır. Cümle artık olayı adlandırıyor. */
          `${worldChanged ? `${capText(world, SHOT_WORLD_MAX_CHARS)}. ` : ''}The elements converge here and reveal the product among them, unchanged: ${same}.`
        : index === 0
          ? options.hasSourceImage
            ? `Product video from the supplied photo${subject ? ` of ${subject}` : ''}${
                ref ? `; ${ref} is that exact product` : ''
              }.`
            : `Product video of ${subject || 'the product'}.`
          : worldChanged
            ? `Hard cut to a new place: ${capText(world, SHOT_WORLD_MAX_CHARS)}. Same product, unchanged: ${same}.`
            : `Same product and setting as the previous shot: ${same}.`;

    /* 🔴 HAREKET PAYI ARTIK DİNAMİK — 20 Ağu 2026.
       Sabit 220 karakter, uzun bir kesme cümlesiyle (yeni mekân + kimlik
       kuyruğu) birleşince süreklilik listesini bütçe dışına itiyordu: ölçülen
       çekimlerde "Keep exactly:" satırı hiç yazılmıyordu ve ürünü koruyan tek
       şey kalan tek cümle oluyordu. Artık daralan şey hareket TARİFİ, ürünün
       kimliği değil — ama tabanı var (`SHOT_DETAIL_MIN_CHARS`), yoksa "sıkıcı
       video" arızası geri gelirdi. */
    /* Ürünsüz çekimde süreklilik maddeleri (ürünün silüeti, logosu) hiçbir
       şeyi kilitlemiyor — pay da ayrılmıyor, o karakterler harekete gidiyor. */
    const shotContinuity = inFrame ? continuity : [];
    const reserve = continuityReserve(shotContinuity);
    /* Müzik ve kapanış cümlesi bütçede HAREKETTEN ÖNCE yer alıyor: ikisi de
       ölçülmüş bir arızanın karşılığı, hareket tarifi ise zaten dinamik. */
    const closing = index === timed.shots.length - 1 ? closingLine(Boolean(music)) : '';
    const fixedLines =
      (musicLine.length > 0 ? musicLine.length + 1 : 0) +
      (closing.length > 0 ? closing.length + 1 : 0);
    const detail = capText(
      [clean(shot.action), clean(shot.camera), clean(shot.focus)]
        .filter((value) => value.length > 0)
        .join('; '),
      Math.max(
        /* Son çekimde taban DAHA DÜŞÜK: kapanış cümlesi zaten hareketin nasıl
           bittiğini söylüyor, yani orada kısalan şey tekrar değil tarif. */
        closing.length > 0 ? SHOT_CLOSING_DETAIL_MIN_CHARS : SHOT_DETAIL_MIN_CHARS,
        Math.min(SHOT_DETAIL_MAX_CHARS, promptLimit - opening.length - reserve - fixedLines - 2),
      ),
    );

    const parts = [opening];
    let used = opening.length + 1 + detail.length + 1;

    /* 🔴 MÜZİK VE KAPANIŞ — stil satırının ÖNÜNDE ve süreklilik payına tabi
       DEĞİL. İkisi de kullanıcının bildirdiği iki arızanın doğrudan panzehiri
       (müziksizlik ve havada kalan bitiş); üslup satırı ise iyileştirme. */
    for (const line of [musicLine, closing]) {
      if (line.length === 0) continue;
      if (used + line.length + 1 > promptLimit) continue;
      parts.push(line);
      used += line.length + 1;
    }

    /* 🔴 İLK ÇEKİMDE STİL SATIRI SÜREKLİLİKTEN ÖNCE ALINIYOR.
       İlk yazımda süreklilik bütün bütçeyi yiyordu ve ölçüldü: 509
       karakterlik bir ilk çekimde stil, mekân ve ışığın ÜÇÜ DE düşüyordu.
       Yani planın yaratıcı fikri — kullanıcının ekranda seçtiği şey —
       prompt'a hiç girmiyordu ve geriye renk listesi kalıyordu.

       Süreklilik ürünün BOZULMAMASINI sağlıyor, stil videonun İZLENİR
       olmasını. İkisinden birini tamamen feda etmek yerine stile bir satırlık
       yer garanti ediliyor; kalan bütçeyi süreklilik alıyor. */
    const guaranteed = index === 0 ? [styleLine, montage ? setLine : ''] : [];
    const written = new Set<string>();
    /* 🔴 SÜREKLİLİĞE AYRILMIŞ PAY — 20 Ağu 2026.
       Garanti satırları bütçeyi ÖNCE alıyordu ve uzun bir stil + mekân
       satırıyla ilk çekimde süreklilik listesinden geriye tek madde bile
       kalmayabiliyordu. Yani ürünün kimliğini koruyan cümle, kaynak
       fotoğrafın gördüğü tek çekimde düşüyordu.
       Ayrılan pay ilk iki maddeyi kapsıyor (`CONTINUITY_RESERVE_ITEMS`) —
       kilit maddeler sıranın başında (silüet, sonra marka işareti). Kalan
       maddeler eskisi gibi artan yerden besleniyor. */
    for (const line of guaranteed) {
      if (line.length === 0 || written.has(line)) continue;
      if (used + line.length + 1 > promptLimit - reserve) continue;
      parts.push(line);
      written.add(line);
      used += line.length + 1;
    }

    /* Süreklilik: sığan madde alınıyor, sığmayan ATLANMIYOR — döngü orada
       BİTİYOR. Sıra önemliydi (marka işareti başta) ve ortadan madde
       toplamak o sırayı bozardı. */
    const fitted: string[] = [];
    for (const item of shotContinuity) {
      const cost = (fitted.length === 0 ? 'Keep exactly: '.length : 2) + item.length + 1;
      if (used + cost > promptLimit) break;
      fitted.push(item);
      used += cost;
    }
    if (fitted.length > 0) parts.push(`Keep exactly: ${fitted.join('; ')}.`);

    /* Kalan ortam satırları (mekân, ışık) — yalnız ilk çekimde ve yalnız yer
       kalırsa. Kaynak görsel varken zaten kareye demirleniyor. */
    if (index === 0) {
      for (const line of [setLine, lightLine]) {
        if (line.length === 0 || written.has(line)) continue;
        if (used + line.length + 1 > promptLimit) break;
        parts.push(line);
        written.add(line);
        used += line.length + 1;
      }
    }

    parts.push(`${detail}.`);

    return {
      seconds: seconds[index] as number,
      /* Son kırpma bir GÜVENLİK AĞI, plan değil: bütçe hesabı yukarıda
         yapıldı. Buraya kadar taşan bir metin, hesapta bir hata olduğunu
         söyler — ama 422 alıp kullanıcının kredisini yakmaktan iyidir. */
      prompt: capText(parts.join(' ').replace(/\s+/g, ' ').trim(), promptLimit),
    };
  });

  return { shots, negativePrompt: buildNegative(timed, options) };
}

/// Süreklilik listesinden bütçede YER AYRILAN madde sayısı. İkisi, çünkü
/// listenin başındaki iki madde ürünün kimliğini taşıyan ikisi: silüet kilidi
/// ve (varsa) marka işareti.
const CONTINUITY_RESERVE_ITEMS = 2;

/**
 * Süreklilik maddeleri için ayrılan karakter payı.
 *
 * Garanti satırları (stil, mekân) bu payı YİYEMİYOR. Pay listenin gerçek
 * uzunluğundan hesaplanıyor — sabit bir sayı, kısa listelerde stili boş yere
 * kırpardı.
 */
function continuityReserve(items: string[]): number {
  const head = items.slice(0, CONTINUITY_RESERVE_ITEMS);
  if (head.length === 0) return 0;
  return head.reduce(
    (sum, item, index) => sum + item.length + (index === 0 ? 'Keep exactly: '.length + 1 : 2),
    0,
  );
}

/**
 * Metni sınıra indirir — KELİME sınırından.
 *
 * Karakterden kesmek "the white cupso" gibi yarım bir kelime bırakıyor ve
 * model onu uydurma bir sözcük olarak okuyor. Nokta korunmuyor: çağıran
 * taraf kendi noktalamasını ekliyor.
 */
function capText(text: string, max: number): string {
  if (text.length <= max) return text;
  const head = text.slice(0, max);
  const wordEnd = head.lastIndexOf(' ');
  return (wordEnd > max / 2 ? head.slice(0, wordEnd) : head).trim().replace(/[.,;:]+$/, '');
}

/**
 * Kesirli çekim sürelerini TAM SANİYEYE dağıtır, toplamı KORUYARAK.
 *
 * 🔴 Sağlayıcı çekim süresini dize enum olarak alıyor ('1'…'15') — kesir yok.
 * Her çekimi ayrı ayrı yuvarlamak toplamı bozuyor: 2,5 + 2,5 + 3 = 8 ama
 * yuvarlanınca 3 + 3 + 3 = 9 ve video istenenden bir saniye uzun (ve bir
 * saniye pahalı) çıkıyor.
 *
 * En büyük artık yöntemi: herkes tabanını alır, artan saniyeler kesri en
 * büyük olanlara dağıtılır. Taban 1 — sağlayıcının alt sınırı ve zaten
 * "çekim" sayılabilecek en kısa süre.
 */
export function wholeSeconds(parts: number[], total: number): number[] {
  if (parts.length === 0) return [];
  const target = Math.max(parts.length, Math.round(total));

  const floors = parts.map((value) => Math.max(1, Math.floor(value)));
  let used = floors.reduce((sum, value) => sum + value, 0);

  /* Taban bile hedefi aşıyorsa (çok çekim, kısa video) fazlalık en uzun
     çekimlerden geri alınıyor — hiçbiri 1'in altına inmeden. */
  while (used > target) {
    let widest = -1;
    for (let i = 0; i < floors.length; i += 1) {
      if (
        (floors[i] as number) > 1 &&
        (widest === -1 || (floors[i] as number) > (floors[widest] as number))
      ) {
        widest = i;
      }
    }
    if (widest === -1) break;
    floors[widest] = (floors[widest] as number) - 1;
    used -= 1;
  }

  const remainders = parts
    .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
    .sort((a, b) => b.fraction - a.fraction);

  let cursor = 0;
  while (used < target) {
    const slot = remainders[cursor % remainders.length] as { index: number };
    floors[slot.index] = (floors[slot.index] as number) + 1;
    used += 1;
    cursor += 1;
  }

  return floors;
}

export interface CompileVideoPlanOptions {
  durationSeconds: number;
  aspectRatio: string;
  /**
   * Kaynak görsel gönderiliyor mu (`image-to-video`).
   *
   * 🔴 Prompt'un AÇILIŞINI değiştiriyor ve bu kozmetik değil: görselden
   * üretimde modele "bu ürünü yarat" demek, elindeki kareyi yok sayıp yeni
   * bir ürün çizmesine yol açıyor. O yolda cümle "kadraja bu üründen başla"
   * ile kuruluyor.
   */
  hasSourceImage: boolean;
  /// Ürün künyesi — süreklilik bloğu ondan besleniyor. Yoksa plan tek başına
  /// derleniyor (kullanıcı planı elle yazmış olabilir).
  product?: ProductFacts | null;
  /// Ortam okuması — `isolatedProduct` ışık sürekliliği cümlesini açıyor.
  scene?: SceneFacts | null;
  /**
   * 🔴 ÜRÜN REFERANS JETONU — sağlayıcıya ürün fotoğrafı bir ELEMAN olarak da
   * gönderildiğinde dolu geliyor ("@Element1").
   *
   * ═════════════════════════════════════════════════════════════════════════
   * Neden bir jeton, neden prompt'un içinde
   * ═════════════════════════════════════════════════════════════════════════
   * `start_image_url` YALNIZ İLK KAREYİ bağlıyor. Çok çekimli üretimde
   * (`multi_prompt`) her kesmeden sonra model ürünü METİNDEN yeniden çiziyor
   * ve ölçülen bozulma tam orada oluyordu: ikinci çekimde taban rengi kayıyor,
   * üçüncüde logo başka bir yazıya dönüşüyor.
   *
   * Kling v3'ün `elements` alanı bunun karşılığı: ürün fotoğrafı bir NESNE
   * olarak yükleniyor ve prompt içinde `@Element1` diye anılıyor. Anılmadığı
   * çekimde eleman devrede DEĞİL — jeton bu yüzden derleyiciye kadar geliyor,
   * sağlayıcı dosyasında prompt'un başına eklenen bir cümle olarak değil:
   * her ÇEKİM kendi cümlesinde onu anmak zorunda.
   *
   * `null` = eleman gönderilmiyor (kaynak fotoğraf yok ya da metinden üretim).
   * O durumda cümleler eskisi gibi kuruluyor ve hiçbir yerde `@` geçmiyor —
   * tanımlanmamış bir elemana atıf, modele anlamsız bir simge göstermek olurdu.
   */
  productRef?: string | null;
  /**
   * 🔴 SES ÜRETİLİYOR MU (`generate_audio`).
   *
   * ═════════════════════════════════════════════════════════════════════════
   * Neden derleyicinin bunu bilmesi gerekiyor
   * ═════════════════════════════════════════════════════════════════════════
   * Sağlayıcı sesi AYRI bir modelden değil, aynı prompt'tan üretiyor. Yani
   * "hangi müzik" sorusunun cevabı da sahne metninin içinde olmak zorunda.
   * Metin müzikten hiç söz etmezse model gördüğü şeyin ortam sesini uyduruyor:
   * kullanıcının bildirdiği "ya saçma ya da müziksiz, sadece ortam efekti"
   * tam olarak bu.
   *
   * Ses KAPALIYKEN müzik cümlesi YAZILMIYOR — üretilmeyecek bir şeyi tarif
   * etmek, 506 karakterlik çekim bütçesinden çalmak demek.
   */
  withAudio?: boolean;
}

/**
 * Planı tek bir sahne yönergesine çevirir.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden bir ŞABLON değil de bir derleyici
 * ═══════════════════════════════════════════════════════════════════════════
 * `video-prompt.ts`teki şablonlar `{{brief}}` gibi yer tutucular taşıyor ve
 * boş bırakılan yer siliniyor. Burada girdi bir NESNE: hangi alanların dolu
 * olduğu her planda değişiyor ve üç boş alanı olan bir şablon cümlesi
 * ("shot on a , lit by a ") dilbilgisi olarak ayakta kalamazdı.
 *
 * Derleyici parça parça kuruyor: dolu olan alan cümleye giriyor, boş olan hiç
 * yazılmıyor. Çıktı her zaman geçerli İngilizce.
 *
 * ⚠️ ÇIKTI SIRASI ÖNEMLİ. Difüzyon tabanlı video modellerinde prompt'un BAŞI
 * daha ağır basıyor; sıra: konu → SÜREKLİLİK → stil → çekim listesi → ortam →
 * ışık → kamera → tempo → palet.
 *
 * 🔴 20 Ağu 2026: süreklilik SONDAN İKİNCİ SIRAYA alındı. Eskiden "bir
 * kısıt, sahnenin konusu değil" diye sona yazılıyordu; ölçüm bunun yanlış
 * olduğunu gösterdi (gerekçenin tamamı gövdede, bloğun başında).
 *
 * 🔴 Aynı gün, `hasSourceImage` olduğunda ortam blokları KISALDI: model
 * kaynak kareye demirlediği için `surface`/`background`/palet prompt'u
 * seyreltmekten başka bir iş yapmıyordu.
 */
export function compileVideoPlanPrompt(
  plan: VideoPlan,
  options: CompileVideoPlanOptions,
): CompiledVideoPrompt {
  const timed = normalizePlanTiming(plan, options.durationSeconds);
  const parts: string[] = [];

  /* 🔴 `name` DEĞİL `subjectEn`: konu cümlesi modele gidiyor ve `name`
     kullanıcının dilinde. Gerekçe `productFactsSchema.subjectEn` başlığında. */
  const subject = options.product?.subjectEn ?? '';
  const ref = clean(options.productRef ?? '');
  /* 🔴 "HER ÇEKİMDE" ARTIK KOŞULLU — 21 Ağu 2026.
     Ürünün kendi içeriğinden doğduğu konseptte açılış çekimlerinde ürün
     kadrajda yok (`videoShotSchema.productInFrame`). "Appears in every shot"
     demek o planla doğrudan çelişirdi ve çelişkiyi model kendi çözer: ürünü
     ürünsüz çekime de koyar. */
  const presence = shotProductPresence(timed, options.hasSourceImage);
  const everyShot = presence.every((value) => value);
  /// Ürünsüz evresi olan plan = OLUŞUM planı. Montaj dili, evre başlıkları ve
  /// "kesme yok" vaadinin işlenişi buna bakıyor.
  const formation = !everyShot;
  /// Cümlenin TAMAMI seçiliyor, ortasına sokulan bir parça değil: parça olarak
  /// tutulduğunda "appears in every shot it appears in unchanged" gibi
  /// tekrarlar çıkıyordu.
  const appears = everyShot
    ? 'appears in every shot unchanged'
    : 'appears unchanged in every shot it is in';
  const opening = options.hasSourceImage
    ? `Commercial product video that starts from the supplied photograph${
        subject ? ` of ${clean(subject)}` : ''
      } and brings it to life.${ref ? ` ${ref} is that exact product and ${appears}.` : ''}`
    : `Commercial product video of ${clean(subject) || 'the product'}.`;
  parts.push(opening);

  /* ═════════════════════════════════════════════════════════════════════
     🔴 SÜREKLİLİK BLOĞU — 20 Ağu 2026'da SONDAN İKİNCİ SIRAYA taşındı
     ═════════════════════════════════════════════════════════════════════
     Eski gerekçe şuydu: "süreklilik bir KISIT, sahnenin konusu değil, o
     yüzden sona yazılıyor." Ölçüm bunu çürüttü.

     20 Ağu 2026'da üretilen iki ürün videosunda modelin bozduğu tek şey
     ürünün kendisiydi: planı "tek çapraz cırt cırt bandı, bağcık yok"
     diyen bir Samba videosunda 8 saniyenin 5'inde bağcık belirdi, burun
     kısmı çöktü ve yazı eridi. Sahne, ışık ve tempo ise sorunsuzdu.

     Yani ürün reklamında ürünün KİMLİĞİ sahnenin konusudur; bir kısıt
     değil. Difüzyon tabanlı video modellerinde prompt'un başı ağır
     bastığı için blok konuyu tanımlayan cümlenin hemen ardına alındı.

     ⚠️ Çekim listesinin ÖNÜNDE ama açılışın ARDINDA: en başa koymak
     modelin ne çektiğini değil neye baktığını anlatan bir liste ile
     başlamak olurdu ve hareket zayıflıyordu. */
  const continuity = continuityItems(timed, options);
  if (continuity.length > 0) {
    /* "Product continuity — a; b; c." değil TAM CÜMLE. Başlık biçimli bir
       etiket, modele bir bölüm başlığı gibi görünüyor ve zayıf bağlanıyor;
       emir kipindeki bir cümle doğrudan uygulanıyor. */
    const anchor = options.hasSourceImage
      ? `The product must stay identical to the photograph in every frame it appears in${
          ref ? ` (${ref})` : ''
        } — the same physical object, not a similar one, never redesigned or restyled`
      : `The product must stay identical in every frame — the same physical object, and it ${appears}`;
    parts.push(`${anchor}: ${continuity.join('; ')}.`);
  }

  if (timed.styleKeywords.length > 0) {
    parts.push(`Overall style: ${joinList(timed.styleKeywords)}.`);
  }

  /* ═════════════════════════════════════════════════════════════════════
     ÇEKİM LİSTESİ — zaman damgalı VE kesmeler AÇIKÇA yazılı
     ═════════════════════════════════════════════════════════════════════
     Zaman damgası eskiden de vardı ("then" ile zincirlemek 4 saniyelik bir
     videoda üç çekimi eşit sanmasına yol açıyordu). 20 Ağu 2026'da eklenen
     şey KESME DİLİ.

     🔴 Eski biçim çekimleri `|` ile ayırıyordu: "0–3s: … | 3–5.5s: …".
     Boru işareti modele hiçbir şey söylemiyor — bir liste ayıracı, bir
     montaj kararı değil. Model onu "aynı çekimin devamı" diye okuyup üç
     beat'i tek bir yavaş harekete eriyordu; kullanıcının "sıkıcı" dediği
     şeyin doğrudan kaynağı buydu.

     "CUT TO" senaryo dilinde bir kesme demek ve video modelleri bunu
     tanıyor. Baştaki sayım ("3 shots, hard cuts between them") kaç kesme
     beklendiğini modelin saymasına bırakmıyor.

     ⚠️ TEK ÇEKİMDE kesme dili YAZILMIYOR: "1 shot, hard cuts between them"
     olmayan bir kesmeyi vaat etmek olurdu ve model karenin ortasında sebepsiz
     bir kesme uydururdu. */
  /* 🔴 MEKÂN DEĞİŞİMİ ÇEKİM SATIRINDA YAZILI — 20 Ağu 2026.
     Ortam serisi olan bir konseptte her çekim başka bir yerde geçiyor
     (`videoShotSchema.setting`). Bu yol tek bir metin ürettiği için değişimi
     satırın kendisi söylemek zorunda: yalnız "CUT TO" yazmak, modele kadraj
     değişimi anlatıyor, DÜNYA değişimi değil. */
  const worlds = shotWorlds(timed);
  const worldChanges = worlds.some((world, index) => index > 0 && world !== worlds[index - 1]);

  let cursor = 0;
  const shotLines = timed.shots.map((shot, index) => {
    const from = cursor;
    cursor = round1(cursor + shot.seconds);
    const detail = [clean(shot.action), clean(shot.camera), clean(shot.focus)]
      .filter((value) => value.length > 0)
      .join('; ');
    const world = worlds[index] ?? '';
    const moved = index > 0 && world.length > 0 && world !== worlds[index - 1];
    /* Ürünsüz çekim satırda AÇIKÇA söyleniyor: tek metinli yolda cümlenin
       kendisinden başka bir kanal yok. */
    /* Oluşum planında satır bir ÇEKİM değil bir EVRE: numaralandırmak "burada
       kesme var" demek olurdu. Zaman damgası duruyor — akışın nerede olduğunu
       söyleyen tek şey o. */
    const absent = !(presence[index] ?? true);
    const head = formation
      ? `${formatSeconds(from)}–${formatSeconds(cursor)}s${
          absent ? ' (product not in frame, only its substance)' : ''
        }`
      : `Shot ${index + 1} (${formatSeconds(from)}–${formatSeconds(cursor)}s)${
          absent ? ' — product not in frame, only its world' : ''
        }`;
    return moved ? `${head} in ${world}: ${detail}` : `${head}: ${detail}`;
  });
  if (shotLines.length === 1) {
    parts.push(`${shotLines[0] as string}.`);
  } else if (formation) {
    /* ═════════════════════════════════════════════════════════════════════
       🔴 OLUŞUM PLANI KESİLMİYOR — 21 Ağu 2026, referans videodan sonra
       ═════════════════════════════════════════════════════════════════════
       Kullanıcının örnek verdiği 12 saniyelik videoda TEK BİR sert kesme yok
       (ölçüldü: en yüksek sahne değişim skoru 0,10 — kesme eşiği 0,2). Ürün
       ilk karede dağılıyor, madde akıyor, içerik ışıktan beliriyor, dünya
       gerçeğe iniyor ve ürün bir ışık süpürmesinin ardında TAMAMLANMIŞ olarak
       kalıyor. Birleşmenin okunmasını sağlayan şey tam olarak bu
       kesintisizlik: kesme, "ürün oluştu" yerine "ürün oraya kondu" diyor.

       Bu yüzden montaj cümlesi burada TERSİNİ söylüyor ve evreler "CUT TO"
       ile değil "THEN, without a cut" ile bağlanıyor. */
    parts.push(
      'One continuous take, no cuts: a single transformation that flows from the first ' +
        `frame to the last. ${shotLines.join('. THEN, without a cut, ')}.`,
    );
  } else if (shotLines.length > 1) {
    parts.push(
      `Edit: ${shotLines.length} shots with a hard cut between each one` +
        `${worldChanges ? ', each cut landing in a different place while the product stays identical' : ''}. ` +
        `${shotLines.join('. CUT TO ')}.`,
    );
  }

  /* ═════════════════════════════════════════════════════════════════════
     🔴 ORTAM BLOKLARI KAYNAK GÖRSEL VARKEN KISALIYOR — 20 Ağu 2026
     ═════════════════════════════════════════════════════════════════════
     `image-to-video` ucunda model kaynak kareye DEMİRLİYOR: mekânı
     değiştirmiyor. Ölçüldü — planı "shaded concrete underpass at dusk"
     diyen bir iş, kaynak fotoğraftaki siyah stüdyo fonu ve kömür
     kütükleriyle çıktı. `surface` ve `background` zaten karenin içinde,
     palet ise doğrudan piksellerde; ikisini de yazmak prompt'u
     seyreltmekten başka bir iş yapmıyordu.

     Kısaltma SİLME DEĞİL: `location` ve ışığın `key`i duruyor, çünkü
     kamera geri çekildiğinde kadraja giren yeni alanı yalnız onlar
     anlatabiliyor. Düşen alanlar (`surface`, `background`, `fill`,
     `accent`, palet) kaynak görselde zaten GÖRÜLÜYOR.

     ⚠️ Kaynak görsel YOKKEN hiçbir şey değişmedi: orada sahneyi sıfırdan
     kuran tek şey bu bloklar. */
  const setting = (
    options.hasSourceImage
      ? [worlds[0] ?? '']
      : [worlds[0] ?? '', clean(timed.setting.surface), clean(timed.setting.background)]
  ).filter((value) => value.length > 0);
  if (setting.length > 0) parts.push(`Set: ${setting.join(', ')}.`);
  if (timed.setting.props.length > 0) {
    parts.push(`Set dressing: ${joinList(timed.setting.props)}.`);
  }

  const lighting = (
    options.hasSourceImage
      ? [clean(timed.lighting.key)]
      : [clean(timed.lighting.key), clean(timed.lighting.fill), clean(timed.lighting.accent)]
  ).filter((value) => value.length > 0);
  if (lighting.length > 0) {
    const mood = clean(timed.lighting.mood);
    parts.push(`Lighting: ${lighting.join(', ')}${mood ? `, ${mood} mood` : ''}.`);
  }

  const camera = [clean(timed.camera.lens), clean(timed.camera.angle), clean(timed.camera.movement)]
    .filter((value) => value.length > 0)
    .join(', ');
  if (camera) parts.push(`Camera: ${camera}.`);

  /* ═════════════════════════════════════════════════════════════════════
     🔴 TEMPO SATIRI KENDİ MONTAJIMIZLA ÇELİŞEMEZ — 20 Ağu 2026
     ═════════════════════════════════════════════════════════════════════
     Derleyici birkaç satır yukarıda "Edit: 3 shots with a hard cut between
     each one" yazdı. Aynı prompt'un altında "Pacing: … single take, no cuts"
     durursa model iki zıt emir alıyor ve ikisinin de olmayan bir ortasını
     üretiyor: kesme yerine iki kadraj arasında eriyen bir geçiş.

     Bu bir ihtimal değil, KAYITLI DURUM: 20 Ağu 2026'da veritabanındaki
     yedi başarılı analizin altısı bu dili taşıyordu, çünkü hepsi eski
     yönergeyle ("Every concept must be shootable AS ONE CONTINUOUS CLIP")
     yazılmıştı. Analizler önbellekten yeniden kullanılıyor — yani düzeltme
     yalnız yeni analizlerde olsaydı, eski fotoğraflar çelişkili prompt
     üretmeye devam ederdi.

     ⚠️ SİLİNEN ŞEY DAR: yalnız "kesme yok" VAADİ, virgülle ayrılmış parça
     olarak. Planın kendi tempo tarifi ("weightless slow motion") DURUYOR —
     o bir üslup tercihi ve kullanıcı onu ekranda görüp onayladı. Tek çekimli
     planda hiçbir şey silinmiyor: orada vaat doğru.

     Kalıcı çözüm yeniden analiz; bu yalnız çelişkiyi kaldırıyor. */
  /* 🔴 OLUŞUM PLANINDA VAAT DOĞRU, SİLİNMİYOR — 21 Ağu 2026.
     Silme, derleyicinin yazdığı "hard cut between each one" cümlesiyle
     çelişmesin diye vardı. Oluşum planında derleyici tam TERSİNİ yazıyor
     ("One continuous take, no cuts"), yani planın "no cuts" demesi artık
     çelişki değil pekiştirme. */
  const keepCutPromise = timed.shots.length <= 1 || formation;
  const pacing = dropCutPromise(clean(timed.motion.pacing), keepCutPromise);
  const transition = dropCutPromise(clean(timed.motion.transition), keepCutPromise);
  if (pacing || transition) {
    parts.push(`Pacing: ${[pacing, transition].filter(Boolean).join(', ')}.`);
  }

  /* ═════════════════════════════════════════════════════════════════════
     🔴 MÜZİK — 20 Ağu 2026'da bağlandı
     ═════════════════════════════════════════════════════════════════════
     `audioDirection` şemada vardı ve prompt'a hiç girmiyordu; sağlayıcı
     sesi aynı metinden ürettiği için ortaya reklamın müziği değil sahnenin
     ortam efekti çıkıyordu. Gerekçenin tamamı `musicBrief` başlığında. */
  const music = musicBrief(timed, options);
  if (music) {
    parts.push(`Soundtrack: ${music}. Instrumental only: no voiceover, no lyrics, no sung words.`);
  }

  /* 🔴 AÇILIŞ VE KAPANIŞ — süreye bağlı bir YAY, sıralı kadrajlar değil.
     Sağlayıcı istenen saniyede kesiyor: son çekimin hareketi bitmemişse
     video cümle ortasında kesilmiş gibi duruyor ("bitişi havada kalıyor").
     Kapanış cümlesi bunu metinde çözüyor; süre de yazılı olduğu için model
     yayı ona göre kuruyor. */
  parts.push(
    `Structure: the first shot opens with movement already happening; over ${options.durationSeconds} seconds the beats build, and ${closingLine(Boolean(music)).slice(CLOSING_PREFIX.length)}`,
  );

  /* PALET — hex'in YANINA renk ADI yazılıyor.
     Çıplak "#c9a882" difüzyon tabanlı bir video modeline neredeyse hiçbir şey
     söylemiyor; "travertine beige (#c9a882)" söylüyor. Ad, ürün künyesindeki
     renk listesinden eşleştiriliyor (model onları zaten İngilizce yazdı) —
     eşleşme yoksa hex tek başına kalıyor, uydurma bir ad üretilmiyor.

     🔴 KAYNAK GÖRSEL VARKEN HİÇ YAZILMIYOR: palet o karenin kendisinde ve
     üstelik süreklilik bloğu ürünün renklerini zaten adıyla sabitliyor.
     İkisini birden yazmak, aynı şeyi iki kez söyleyip birini hex'e
     çevirmekti. */
  if (!options.hasSourceImage) {
    const colourNames = new Map(
      (options.product?.colors ?? []).map((colour) => [
        colour.hex.toLowerCase(),
        clean(colour.name),
      ]),
    );
    const palette = [timed.palette.primary, timed.palette.accent, timed.palette.background]
      .filter((value): value is string => typeof value === 'string')
      .map((hex) => hex.toLowerCase())
      .map((hex) => {
        const name = colourNames.get(hex);
        return name ? `${name} (${hex})` : hex;
      });
    if (palette.length > 0) parts.push(`Colour palette: ${palette.join(', ')}.`);
  }

  /* IŞIK SÜREKLİLİĞİ — yalnız kaynak görsel varken ve ürün İZOLE DEĞİLKEN.
     İzole (beyaz fon) bir üründe kaynak ışığı korumak, yeni sahnenin ışığını
     baştan sakatlıyor: stüdyo aydınlatması bir sokak sahnesinde sahte
     duruyor. */
  if (options.hasSourceImage && options.scene && !options.scene.isolatedProduct) {
    const direction = clean(options.scene.lightDirection);
    if (direction) parts.push(`Keep the light direction of the source photo: ${direction}.`);
  }

  parts.push(`Framed for ${options.aspectRatio}, ${options.durationSeconds} seconds total.`);

  return {
    prompt: parts.join(' '),
    negativePrompt: buildNegative(timed, options),
  };
}

/**
 * Olumsuz prompt — planınki + ürüne özel türetmeler.
 *
 * Genel liste `video-prompt.ts`te (`DEFAULT_NEGATIVE_PROMPT`) ve orada
 * KALIYOR: sağlayıcı çağrısını o dosya kuruyor ve iki listenin birleşimi
 * orada yapılıyor. Burada yalnız PLANA özgü olanlar üretiliyor — ikisini de
 * burada birleştirmek, plan yokken (`brief` yolu) hangi listenin geçerli
 * olduğunu iki yerde tanımlamak olurdu.
 */
/// Çekim elemanında müzik cümlesine ayrılan tavan. Müzik her çekimde
/// TEKRARLANIYOR (aşağıdaki gerekçe) ve 506'lık bütçede uzun bir müzik tarifi,
/// çekimin ne olduğunu anlatan cümleyi yerinden eder.
const SHOT_MUSIC_MAX_CHARS = 70;

/**
 * MÜZİK CÜMLESİ — planın `audioDirection` alanından.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 `audioDirection` BUGÜNE KADAR HİÇBİR YERE GİTMİYORDU
 * ═══════════════════════════════════════════════════════════════════════════
 * Alan şemada vardı, model dolduruyordu, kullanıcı ekranda görüyordu — ve
 * prompt'a hiç girmiyordu. Yani sağlayıcının ürettiği ses, sahne tarifinden
 * uydurduğu ortam efektinden ibaretti. Ölü alan deseninin bir örneği daha:
 * içeriği denetimsiz kalıyor, çünkü kimse çıktıda karşılığını göremiyor.
 *
 * ⚠️ VOKAL AÇIKÇA YASAKLANIYOR. Sağlayıcı konuşma da üretebiliyor ("supports
 * Chinese and English voice output") ve reklam videosunda uydurulmuş bir ses,
 * anlaşılmayan bir mırıltı olarak çıkıyor.
 *
 * Plan bir şey yazmamışsa (eski analizler) cümle YİNE kuruluyor ama uydurma
 * bir tür seçilmiyor: tempo planın kendi `motion.pacing` alanından okunuyor.
 * Uydurma bir tür, kullanıcının "saçma müzik" dediği şeyin ta kendisi olurdu.
 */
function musicBrief(plan: VideoPlan, options: CompileVideoPlanOptions): string {
  if (!options.withAudio) return '';
  const written = clean(plan.audioDirection ?? '');
  if (written) return written;
  const pacing = clean(plan.motion.pacing);
  return pacing
    ? `instrumental music bed that matches the ${pacing}`
    : 'instrumental music bed that matches the pace of the cuts';
}

/**
 * KAPANIŞ CÜMLESİ — videonun havada kalmaması için.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden bu bir prompt işi
 * ═══════════════════════════════════════════════════════════════════════════
 * Sağlayıcı istenen saniyede kesiyor; son çekimin hareketi o ana kadar
 * bitmemişse video cümlenin ortasında kesilmiş gibi duruyor. Kullanıcının
 * "bitişi havada kalıyor" dediği şey bu ve düzeltmesi montajda değil METİNDE:
 * son çekime "burada bitiyor" demek.
 *
 * ⚠️ Süreyle birlikte anlam kazanıyor: son çekim 1 saniyeyse yerleşecek vakit
 * yok. O yüzden derleyici son çekime en az 2 saniye ayırıyor
 * (`ensureClosingBeat`).
 */
/**
 * Kapanış cümlesinin ÖNEKİ — sabit, çünkü tek metinli derleyici cümleyi kendi
 * cümlesinin içine gömerken bu öneki kırpıyor.
 *
 * 🔴 Kırpma uzunluğu `'Final beat: '.length` (12) yazıyordu ama önek
 * `'Ends here: '` (11): cümlenin ilk harfi de gidiyordu ve ÜRETİLEN HER plan
 * prompt'unda "…and he motion settles…" duruyordu. Bir zamanlar cümle "Final
 * beat: …" diye başlıyordu, yeniden yazılınca sayı geride kaldı.
 */
const CLOSING_PREFIX = 'Ends here: ';

function closingLine(withAudio: boolean): string {
  return withAudio
    ? `${CLOSING_PREFIX}the motion settles, the product centred and still, music resolves.`
    : `${CLOSING_PREFIX}the motion settles and the product ends centred and still.`;
}

/**
 * SON ÇEKİME EN AZ 2 SANİYE — kapanışın yerleşmesi için.
 *
 * Dağıtım (`wholeSeconds`) süreyi eşit paylaştırıyor ve tek saniyelik bir son
 * çekim çıkabiliyor (8 saniye / 5 çekim → 2,2,2,1,1). Bir saniyede hareket
 * ne biter ne yerleşir; video yine havada kalır. Fazla saniye EN UZUN
 * çekimden alınıyor ve kimse 1 saniyenin altına inmiyor.
 *
 * ⚠️ Toplam KORUNUYOR: fatura bu sayıdan hesaplanıyor.
 */
function ensureClosingBeat(seconds: number[]): number[] {
  if (seconds.length < 2) return seconds;
  const out = [...seconds];
  const last = out.length - 1;
  while ((out[last] as number) < 2) {
    let widest = -1;
    for (let i = 0; i < last; i += 1) {
      if (
        (out[i] as number) > 1 &&
        (widest === -1 || (out[i] as number) > (out[widest] as number))
      ) {
        widest = i;
      }
    }
    if (widest === -1) break;
    out[widest] = (out[widest] as number) - 1;
    out[last] = (out[last] as number) + 1;
  }
  return out;
}

/**
 * SÜREKLİLİK MADDELERİ — planınki + künyeden türeyenler.
 *
 * İki derleyici de (`compileVideoPlanPrompt` tek metin, `compileVideoPlanShots`
 * çekim çekim) aynı listeyi istiyor ve liste ölçülmüş bir hatanın panzehiri:
 * modelin bozduğu tek şey ürünün kendisiydi. İki yerde iki kopya, bir gün
 * birinin marka maddesini alıp diğerinin almaması demekti.
 */
function continuityItems(plan: VideoPlan, options: CompileVideoPlanOptions): string[] {
  const items = plan.productContinuity.map(clean).filter((value) => value.length > 0);
  /* Marka işareti görünüyorsa madde KOŞULSUZ ekleniyor: modelin en sık
     bozduğu şey metin. */
  if (options.product?.brandVisible) {
    items.unshift('keep the brand mark sharp, correctly spelled and undistorted');
  }
  /* 🔴 `product.shape` BURAYA GİREMEZ — 20 Ağu 2026'da denendi ve GERİ ALINDI.
     Siluet kilidi iyi bir fikirdi ama `shape` KULLANICININ DİLİNDE yazılıyor
     (görü yönergesindeki dil kuralı onu `name`, `category`, `materials` ile
     aynı listeye koyuyor). Gerçek bir analizde alanın içeriği şuydu:
     "Uzun, silindirik, dar gövdeli şişe; üstte omuzdan daralan siyah pompa" —
     yani video modeline Türkçe bir cümle. `name` yerine `subjectEn` kullanma
     gerekçesinin aynısı; İngilizce ikizi olmayan bir alan prompt'a giremez. */
  if (options.product?.colors.length) {
    const colours = options.product.colors
      .map((colour) => clean(colour.name))
      .filter((value) => value.length > 0);
    if (colours.length > 0) {
      items.push(`keep the product colours exactly as they are (${joinList(colours)})`);
    }
  }
  return items;
}

function buildNegative(plan: VideoPlan, options: CompileVideoPlanOptions): string {
  const items = plan.negativePrompt.map(clean).filter((value) => value.length > 0);
  if (options.product?.brandVisible) {
    items.push('misspelled brand name', 'warped logo');
  }
  if (options.scene?.imageQuality.rating === 'poor') {
    // Kaynak zaten zayıfsa modele "keskinleştir" demek yerine bozulmayı
    // yasaklıyoruz: düşük çözünürlüklü bir kareyi büyütmek en sık burada
    // yapay doku üretiyor.
    items.push('upscaling artifacts', 'plastic looking surface');
  }
  return [...new Set(items)].join(', ');
}

// ═══════════════════════════════════════════════════════════════════════════
// Yardımcılar
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Prompt'a girecek metni temizler.
 *
 * `video-prompt.ts` → `sanitizeVideoText` ile aynı iş, aynı gerekçe: kontrol
 * karakterleri ve satır sonları prompt'un içinde sahte bir yönerge bloğu
 * kurmanın en ucuz yolu. Burada ayrıca sondaki noktalama düşürülüyor —
 * derleyici cümleleri kendi noktalıyor ve "slow push-in.." çıkmamalı.
 *
 * ⚠️ İKİZ DEĞİL: o fonksiyon `maxChars` alıyor ve `null` döndürebiliyor
 * (şablon yer tutucusu silinsin diye). Burada uzunluğu ZOD zaten sınırladı ve
 * boş dize yeterli — çağıran taraf `.filter(length > 0)` yapıyor.
 */
/**
 * KÜNYEDEN PROMPT CÜMLESİ — şablon yolunun `{{brief}}` karşılığı.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden var
 * ═══════════════════════════════════════════════════════════════════════════
 * Şablonlu üretimde konu cümlesini kullanıcı yazıyor ve çoğu kullanıcı
 * yazmıyor. Boş bırakıldığında `buildVideoPrompt` yedeğe düşüyor:
 *
 *   "Subject: a product in a modern advertising setting."
 *
 * Yani prompt'un tek anlam taşıyan cümlesi hiçbir şey söylemiyor. 21 Ağu
 * 2026'daki ilk `product-pattern-morph` üretiminde ölçülen kusurun bir
 * parçası buydu: şablon "ürünle canlı aynı gradyanı paylaşsın" diyor ama
 * paylaşılacak gradyanın ne olduğu hiçbir yerde yazmıyor.
 *
 * Analiz o bilgiyi zaten üretiyor. Bu fonksiyon onu tek satıra indiriyor.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 YALNIZ İNGİLİZCE ALANLAR — `name` ve `shape` GİRMİYOR
 * ═══════════════════════════════════════════════════════════════════════════
 * `name` kullanıcının dilinde (`productFactsSchema.name` başlığında gerekçe)
 * ve `shape` da öyle — görü yönergesi ikisini de kullanıcının dilinde
 * yazdırıyor. Video modeline Türkçe bir cümle göndermek bir kez yapıldı ve
 * geri alındı. Buraya giren her alan İngilizce olduğu DOĞRULANMIŞ olanlar:
 * `subjectEn`, `colors[].name`, `materials`, `distinctiveDetails`.
 *
 * ---------------------------------------------------------------------------
 * Sıra bilgi yoğunluğuna göre
 * ---------------------------------------------------------------------------
 * Konu → renkler → malzeme → ayırt edici detay. Kırpma SONDAN olduğu için
 * kesilen ilk şey en az kritik olanı; `subjectEn` hiçbir bütçede düşmüyor.
 */
export const PRODUCT_BRIEF_MAX_CHARS = 320;

export function productBriefLine(product: ProductFacts | null | undefined): string {
  if (!product) return '';

  const subject = clean(product.subjectEn);
  /* Konu YOKSA cümle de yok: renk ve malzeme listesi tek başına bir özne
     kurmuyor ("yellow, orange, mesh" bir ürün tarifi değil) ve modele özneşiz
     bir sıfat yığını göndermek, yedek cümleden daha kötü. */
  if (subject.length === 0) return '';

  const colours = product.colors.map((colour) => clean(colour.name)).filter((n) => n.length > 0);
  /* Renk KONUYA YAPIŞIK, ayrı bir parça değil: "a running shoe, in yellow"
     cümlesindeki virgül konuyu bir kez bitirip yeniden açıyor. */
  const parts: string[] = [colours.length > 0 ? `${subject} in ${joinList(colours)}` : subject];

  const materials = product.materials.map(clean).filter((value) => value.length > 0);
  if (materials.length > 0) parts.push(joinList(materials));

  const details = product.distinctiveDetails.map(clean).filter((value) => value.length > 0);
  if (details.length > 0) parts.push(joinList(details));

  const line = parts.join(', ');
  return line.length > PRODUCT_BRIEF_MAX_CHARS
    ? line.slice(0, PRODUCT_BRIEF_MAX_CHARS).replace(/[\s,]+$/, '')
    : line;
}

function clean(value: string | null | undefined): string {
  if (typeof value !== 'string') return '';
  return (
    value
      // eslint-disable-next-line no-control-regex
      .replace(/[\u0000-\u001f\u007f]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/[.;,]+$/, '')
  );
}

/**
 * "Kesme yok" vaadini tempo metninden düşürür.
 *
 * Virgülle ayrılmış parçalar tek tek eleniyor, metnin tamamı değil: "slow,
 * weightless slow motion, single take, no cuts" → "slow, weightless slow
 * motion". Tüm metni atmak, planın üslubunu da silmek olurdu.
 *
 * ⚠️ Kalıp DAR tutuldu. `continuous` tek başına eşleşmiyor: "one continuous
 * beat" bir tempo tarifi, montaj vaadi değil. Yalnız montajdan söz eden
 * birleşimler (`continuous take`, `single take`, `no cuts`, `uncut`)
 * düşüyor — çünkü çelişkiyi doğuran onlar.
 */
const CUT_PROMISE =
  /\b(no (?:hard )?cuts?|without cuts?|single take|one take|continuous (?:take|shot)|uncut|unbroken)\b/i;

function dropCutPromise(text: string, singleShot: boolean): string {
  if (singleShot || text.length === 0) return text;
  return text
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.length > 0 && !CUT_PROMISE.test(part))
    .join(', ');
}

function joinList(values: string[]): string {
  return values
    .map(clean)
    .filter((value) => value.length > 0)
    .join(', ');
}

/// Zaman damgası: 2 → "2", 2.5 → "2.5". Gereksiz ".0" prompt'u gürültülü
/// yapıyor ve modele hassasiyet vaat ediyor (bu bir montaj listesi değil).
function formatSeconds(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}
